import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
    Button,
    Header,
    Textarea,
    Container,
    SpaceBetween,
    Box,
    Grid,
    Link,
    ExpandableSection,
    FileUpload,
    Modal,
    Spinner,
    Checkbox,
    ColumnLayout,
    Select,
    ButtonDropdown,
    StatusIndicator,
    Popover,
    Table,
    Alert,
    Flashbar,
    Input,
    FormField
} from "@amzn/awsui-components-react";
import { AgGridReact } from 'ag-grid-react';

import './styles.scss'
import { configureFlexicoS3, configureFriscoS3, configureTANCS3 } from 'src/context/AuthContextUtility';
import { Storage } from 'aws-amplify';
import * as d3 from "d3";
import { CellDataType } from 'src/utilities/ag-grid-utils';
import { createPreferenceWithExtras, deletePreference, getBrassAllLDAPS, getPresignedUrlForVersionFile, getRowLocksForSession, getTANCSession, listPreference, listTANCVersions, onNotifyUpdate, tancEnsureRowIdentifiers, tancRowLockEvent, tancSave, tancSubmitRebase, tancSync, tancUpdateSubscribers, updateTANCLock, updatePreferenceWithExtras } from 'src/utilities/AppSyncPlusAPIs';
import { useSelector } from 'react-redux';
import { createAuditLog } from '../Templates/Utils/AppActivityUtils';
import { v4 as uuidv4 } from 'uuid';
import { CUSTOM_TAG, PERSONAL_TAG } from './constants';
import { deepEqual, filterNullsInObjectArrays } from '../utils/generalUtils';
import { getDocumentFromVault, uploadDocumentToVault } from 'src/Ateam-BI/Vault/VaultAPIs';
import { VAULT_SERVICES } from "src/Ateam-BI/Vault/constants";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from 'src/utilities/adminUtils';


// Cell editors can return the same value in another type ("1.50" vs 1.5, "2024-01-01" vs Date,
// a new array), so edits compare values, not types.
const toComparable = (v) => {
    if (v === null || v === undefined) return '';
    if (Array.isArray(v)) return v.map(x => String(x).trim()).join(',');
    return String(v).trim();
};
// Timestamp incl. time of day; a date-only "2024-01-01" is local midnight, like the grid's date parser.
const toTime = (v) => {
    const s = toComparable(v);
    return (v instanceof Date ? v : new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s)).getTime();
};
const isSameCellValue = (a, b) => {
    const x = toComparable(a), y = toComparable(b);
    if (x === y) return true;
    if (a instanceof Date || b instanceof Date) return toTime(a) === toTime(b);
    // Numeric columns: "1.50" vs 1.5, empty vs 0.
    return (typeof a === 'number' || typeof b === 'number') && Number(x || 0) === Number(y || 0);
};

const TANCSession = (props) => {
    const gridRef = useRef();
    const gridWrapperRef = useRef(null);  // grid container, to tell if focus left the grid
    const isPortalAdmin = useSelector(selectIsEffectiveAdmin)
    const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin)
    const currentStage = useSelector((globalState) => globalState.auth.currentStage)
    const userId = useSelector((globalState) => globalState.auth.userId)
    const [dsInfo, setDsInfo] = useState([])
    const [columnsList, setColumnsList] = useState([])
    const [gridInfo, setGridInfo] = useState({ rowCount: 0, totalRows: 0 });
    const [aggregationValues, setAggregationValues] = useState(null)
    const [sessionDetails, setSessionDetails] = useState(null)
    const [annotationLoader, setAnnotationLoader] = useState(null)
    const [annotationError, setAnnotationError] = useState("error..")
    const [undoEditCount, setUndoEditCount] = useState(0)
    const [selectedRowIndex, setSelectedRowIndex] = useState(0)
    const [multiSelectedRow, setMultiSelectedRow] = useState([])
    const [rowGroupColumns, setRowGroupColumns] = useState([])
    const [showAnnotationImportModal, setShowAnnotationImportModal] = useState(false)
    const [showAnnotationImportAppendModal, setShowAnnotationImportAppendModal] = useState(false)
    const [annotationSaveSuccessMessage, setAnnotationSaveSuccessMessage] = useState(null)
    const [showSyncRequestCommentModal, setShowSyncRequestCommentModal] = useState(false)
    const [showHistoryModal, setShowHistoryModal] = useState(false)
    const [syncRequestComment, setSyncRequestComment] = useState(null)
    const [requestSyncCommentErrorMessage, setRequestSyncCommentErrorMessage] = useState(null)
    const [syncLoaderMessage, setSyncLoaderMessage] = useState(null)
    const [historyLoaderMessage, setHistoryLoaderMessage] = useState(null)
    const [historyErrorMessage, setHistoryErrorMessage] = useState(null)
    const [permissions, setPermissions] = useState([])
    const [filters, setFilters] = useState([])
    const [customFilters, setCustomFilters] = useState({})
    const [configurationMap, setConfigurationMap] = useState({});
    const [filterThatApplyToAlias, setFilterThatApplyToAlias] = useState([]);
    const [selectedFilterOption, setSelectedFilterOption] = useState(null);
    const [tancFilterModel, setTANCFilterModel] = useState(null);
    const [viewers, setViewers] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [historyData, setHistoryData] = useState([])
    const [resubmitTimestamp, setResubmitTimestamp] = useState(null)
    const [resubmitVersionData, setResubmitVersionData] = useState(null)
    const [flashMessages, setFlashMessages] = useState([])
    const [showSaveModal, setShowSaveModal] = useState(false)
    const [showSaveCurrentFiltersModal, setShowSaveCurrentFiltersModal] = useState(false)
    const [currentFilterName, setCurrentFilterName] = useState("")
    const [hasFilterDiff, setHasFilterDiff] = useState(false)
    const [saveRequestComment, setSaveRequestComment] = useState(null) 
    const [requestSaveCommentErrorMessage, setRequestSaveCommentErrorMessage] = useState(null)
    const [tancUserRoleMap, setTancUserRoleMap] = useState({})
    const [savedFormulas, setSavedFormulas] = useState(null)
    const [savedColumnFormulas, setSavedColumnFormulas] = useState(null)
    const [formulaPreferenceId, setFormulaPreferenceId] = useState(null)
    const [hasUnsavedFormula, setHasUnsavedFormula] = useState(false)
    const [pendingFormula, setPendingFormula] = useState(null)
    const [pendingFormulaRemoval, setPendingFormulaRemoval] = useState(null)
    const [showColumnFormulaOverrideWarning, setShowColumnFormulaOverrideWarning] = useState(null)
    const annotationLevel = "public"

    useEffect(() => {
        const interval = setInterval(() => {
            notifyViewing()
            cleanUpViewers()
        }, 30000);
        notifyViewing()

        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        if (sessionDetails?.s3Uri) {
            getAnnotationInput()
            notificationUpdates()
            loadRowLockSnapshot()
        }
        return () => {
        };
    }, [sessionDetails?.s3Uri]);

    useEffect(() => {
        return () => {
            tancUpdateSubscribers(sessionDetails?.sessionId, { "type": "VIEWER", "alias": userId, "action": "DEREGISTER" } || {}, function onSuccess(result) {
            }, function onFailure(result) {
            })
        };
    }, [sessionDetails?.sessionId]);

    useEffect(() => {
        if (props?.sessionDetails) {
            setSessionDetails(props.sessionDetails)
        }
        if (props?.tancDetails) {
            loadAllPreferences()
        }
        return () => {
        };
    }, [props]);

    useEffect(() => {
        if (configurationMap && dsInfo) {
            cleanData()
            setColumnDefinition()
        }
        return () => {
        };
    }, [configurationMap]);

    useEffect(() => {
        if (selectedFilterOption) {
            setColumnDefinition()
        }
        return () => {
        };
    }, [selectedFilterOption]);

    useEffect(() => {
        setColumnDefinition()
        return () => {
        };
    }, [sessionDetails?.isLocked, isEditing]);

    useEffect(() => {
        if (tancFilterModel && gridRef?.current?.api) {
            gridRef?.current?.api?.setFilterModel(tancFilterModel);
            gridRef?.current?.api.onFilterChanged();
        }
        return () => {
        };
    }, [tancFilterModel]);

    const defaultProjectColDef = {
        resizable: true,
        sortable: true,
        autoHeaderHeight: true,
        floatingFilter: true,
        headerClass: 'header-center',
        editable: false,
        enableCellChangeFlash: true,
        enableRowGroup: false,
        enableCellChangeFlash: true,
        suppressHeaderMenuButton: false,
        enableHeaderHighlight: true,
        suppressMovable: true,
        cellClassRules: {
            'edited-row': (params) => { return params?.data?.isEdited || params?.data?.isNew },
            'new-row': (params) => { return params?.data?.isNew },
        },
        tooltipValueGetter: (params) => {
            const rid = params?.data?.internal_tanc_identifier;
            if (!isRowLocked(rid)) return null;
            const owner = rowLockOwner(rid);
            // Peer-held → "Locked by X"; my own committed/in-flight row → it's submitted
            // and awaiting the source-of-truth update, so surface that instead.
            return (owner && owner != userId) ? `Locked by ${owner}` : 'Submitted — awaiting update';
        },
        minWidth: 150,
    };

    const sideBar = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
            },
            {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
            }
        ],
        // Hide sidebar by default
        defaultToolPanel: '', // empty string means closed
        // or use 'false' to hide completely
        // defaultToolPanel: false,
    };


    function notificationUpdates() {
        onNotifyUpdate(sessionDetails?.sessionId, function onResult(result) {
            let validationResponse = JSON.parse(result.value.data.onNotifyUpdate.payload)
            // Save broadcasts a combined SYNC_STATUS+RELOAD message. Capture the
            // saver flag before SYNC_STATUS clears it so RELOAD can reload directly.
            const initiatedSave = savingRef.current;
            if (validationResponse?.type == 'SYNC_STATUS'
                && validationResponse?.message
                && validationResponse?.message == 'success') {
                if (rowLockingEnabled) {
                    // FLEXICO: 'success' broadcasts to all, but only the saver exits.
                    // release=false: the orchestrator deletes rows, so UI must not race it.
                    if (savingRef.current) {
                        exitEditing({ release: false });
                    }
                } else {
                    // FRISCO: save success releases the full-session lock, as before.
                    unlockSession();
                }
                savingRef.current = false;
            }

            if (validationResponse?.type == 'SYNC_STATUS'
                && validationResponse?.action == 'NOTIFY'
                && validationResponse?.error) {
                if (initiatedSave) {
                    savingRef.current = false;
                    setAnnotationLoader(null);
                    setFlashMessages([{
                        type: 'error',
                        dismissible: true,
                        dismissLabel: 'Dismiss',
                        onDismiss: () => setFlashMessages([]),
                        header: 'Save failed',
                        content: `Your changes could not be applied: ${validationResponse.error}. Please try saving again.`,
                        id: 'row_lock_save_failed',
                    }]);
                }
            }

            if (validationResponse?.action == 'RELOAD') {
                setResubmitTimestamp(null);
                if (rowLockingEnabled && !initiatedSave && hasLocalEdits()) {
                    setFlashMessages([{
                        type: 'info',
                        dismissible: true,
                        dismissLabel: 'Dismiss',
                        onDismiss: () => setFlashMessages([]),
                        header: 'Sync available',
                        content: 'Another user has saved changes to this view. Update to pull them in — your changes will not be overwritten. Other rows will be synced instead.',
                        action: (<Button onClick={() => { setFlashMessages([]); syncAndRebaseLocalEdits(); }}>Update</Button>),
                        id: 'row_lock_sync_available',
                    }]);
                } else {
                    getAnnotationInput(true)
                }
            }

            if (validationResponse?.type == 'LOCK_STATUS') {
                getTANCSession(sessionDetails?.sessionId, function onSuccess(result){
                    setSessionDetails(result?.data?.getTANCSession)
                }, function onFailure(result){
                    
                })
            }

            if (validationResponse?.type == 'VIEWER'
                && validationResponse?.alias && validationResponse?.action
            ) {
                if (validationResponse?.action == 'REGISTER' && validationResponse?.alias != userId) {
                    // add to set
                    viewers[validationResponse.alias] = Date.now()
                    setViewers({ ...viewers })
                } else if (validationResponse?.action == 'DEREGISTER') {
                    // remove from set
                    setViewers(prevData => {
                        const newData = { ...prevData };
                        delete newData[validationResponse.alias];
                        return newData;
                    });
                }
            }

            if (validationResponse?.type == 'ROW_LOCK_STATUS'
                && validationResponse?.internal_tanc_identifier
            ) {
                const id = validationResponse.internal_tanc_identifier;
                const owner = validationResponse.lockedBy;
                const state = validationResponse.tanc_lock_state;
                const isRelease = validationResponse.action == 'RELEASED';
                // Ignore my own EDITING events: my EDITING row stays editable, and redrawRows()
                // mid-edit would bounce me out. (My EDITING lock lives in heldLocksRef.)
                if (!isRelease && owner == userId && !COMMITTED_LOCK_STATES.has(state)) {
                    return;
                }
                console.log(`[RowLock] recv ${validationResponse.action} row=${id} by=${owner} state=${state} session=${sessionDetails?.sessionId} at ${new Date().toISOString()}`);
                setRowLocks(prev => {
                    const next = { ...prev };
                    if (isRelease) {
                        delete next[id];
                    } else {
                        // Peer's lock (any state) or my own committed/in-flight row —
                        // both render locked until RELEASED.
                        next[id] = { lockedBy: owner, state };
                    }
                    rowLocksRef.current = next;
                    // Re-evaluate editable/row styles for the affected row.
                    gridRef.current?.api?.redrawRows();
                    return next;
                });
            }
        }, function onFailure(error) {
            // console.log(error)
        })
    }
    function notifyViewing() {
        if (props?.sessionDetails?.sessionId) {
            tancUpdateSubscribers(props?.sessionDetails.sessionId, { "type": "VIEWER", "alias": userId, "action": "REGISTER" } || {}, function onSuccess(result) {
            }, function onFailure(result) {
            })
        }
    }
    function cleanUpViewers() {
        const fiveMinutesAgo = Date.now() - (1 * 60 * 1000);

        setViewers(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (newData[key] < fiveMinutesAgo) {
                    delete newData[key];
                }
            });
            return newData;
        });
    }

    const resetGridView = () => {
        if (gridRef.current) {
            const gridApi = gridRef.current.api;
            const columnApi = gridRef.current.columnApi;

            // Clear Filters
            gridApi?.setFilterModel(null);

            // Clear Sorting
            columnApi?.applyColumnState({
                defaultState: { sort: null },
            });

            // Reset Columns
            columnApi?.resetColumnState();

            // Reset Pinned Columns
            columnApi?.setPivotMode(false);

            // // Resize to fit
            // gridApi?.sizeColumnsToFit();

            // Redraw the grid
            gridApi?.redrawRows();
        }
    };

    function getS3PathAndFileName(s3Uri) {
        // Remove 's3://' and split by '/'
        const parts = s3Uri?.replace('s3://', '').split('/');

        // Remove bucket (first element) and filename (last element)
        parts.shift(); // Remove bucket
        const fileName = parts.pop();   // Remove filename
        let path = parts.join('/');
        path = `${path}/`
        return {
            path,
            fileName
        }
    }

    function cleanViewBeforeLoad(resetView = false) {
        setAnnotationLoader('Loading..')
        setAnnotationError(null)
        setDsInfo(null)
        setColumnsList(null)
        setSelectedRowIndex(null)
        setUndoEditCount(0)
        setMultiSelectedRow([])
        setAggregationValues(null)
        if (!resetView) {
            resetGridView()
        }
    }

    function setViewOnTheGrid(result) {
        // assign unique rowIds for AG Grid formulas to find rows/save formulas
        result.forEach((row, index) => {
            row._tancRowId = String(index);
        });
        totalRowsRef.current = result.length;
        nextRowIdRef.current = result.length;
        // Fresh authoritative state loaded: any pending deletes are now either
        // persisted or superseded, so reset the delete tracker.
        deletedIdentifiersRef.current = new Set();
        setDsInfo(result)
        cleanData()
        setColumnDefinition(result)
        setAnnotationLoader(null)
        setAnnotationError(null)
    }

    // FRISCO wizard: edits handed to the parent on Proceed come back when Step 1 reopens
    // (the grid unmounts between steps). Applied once per mount; Cancel clears the draft.
    const draftAppliedRef = useRef(false);
    const takePendingRowLockDraft = () => {
        const draft = props?.pendingRowLockDraft;
        if (!rowLockingEnabled || draftAppliedRef.current || !draft) return null;
        draftAppliedRef.current = true;
        return draft;
    };
    const resumePendingRowLockDraft = (draft) => {
        deletedIdentifiersRef.current = new Set(draft.deletedIds || []);
        (draft.heldIds || []).forEach(id => {
            heldLocksRef.current.add(id);
            sendRowLockEvent(id, 'EDITING', (err) => onAcquireLost(id, err));  // re-assert TTL
        });
        setIsEditing(true);
        console.log(`[RowLock] restored wizard draft session=${sessionDetails?.sessionId} edits=${Object.keys(draft.editedByIdentifier || {}).length} new=${(draft.newRows || []).length} held=${(draft.heldIds || []).length}`);
    };

    const getAnnotationInput = async (resetView = false) => {
        cleanViewBeforeLoad(resetView)
        const sessionFileDetails = getS3PathAndFileName(sessionDetails?.s3Uri)
        let level = annotationLevel
        let prefix = sessionFileDetails['path']
        configureTANCS3()
        let arr = sessionFileDetails['fileName'].split('.')
        let contentType = arr[arr.length - 1]
        // let fileURL = await Storage.get(sessionFileDetails['fileName'], { level: level, customPrefix: { public: prefix }, expires: 60 });
        let document = await getDocumentFromVault(contentType, VAULT_SERVICES.TANC, {"file_path": `${prefix}${sessionFileDetails['fileName']}`, "service_id":`${sessionDetails.tancId}`, "app_id":`${sessionDetails.sessionId}`})                
        let fileURL = document?.fileUrl
        await d3.csv(fileURL).then(async (result) => {
            const needsIdentifiers = rowLockingEnabled && Array.isArray(result) && result.length > 0
                && !('internal_tanc_identifier' in result[0])
            if (needsIdentifiers && !identifierEnsuredRef.current && sessionDetails?.sessionId) {
                identifierEnsuredRef.current = true
                try {
                    await tancEnsureRowIdentifiers(sessionDetails.sessionId, sessionDetails.tancId)
                } catch (e) { /* fall through and render what we have */ }
                return getAnnotationInput(resetView)
            }
            const draft = takePendingRowLockDraft()
            setViewOnTheGrid(draft ? mergeLocalEdits(result, draft) : result)
            if (draft) resumePendingRowLockDraft(draft)
        }).catch((response) => {
            setAnnotationLoader(null)
            setAnnotationError('Loading failed')
        });
    }

    // Pull an incoming sync without losing in-progress edits: fetch the latest
    // truth and rebase the user's pending delta on top, preserving edit state.
    // Pending local edits (edited rows by id, new rows, deletes, held locks) from the grid.
    const snapshotLocalEdits = () => {
        const editedByIdentifier = {};
        const newRows = [];
        gridRef.current?.api?.forEachNode((node) => {
            const d = node?.data;
            if (!d) return;
            if (d.isNew) {
                newRows.push({ ...d });
            } else if (d.isEdited && d.internal_tanc_identifier) {
                editedByIdentifier[d.internal_tanc_identifier] = { ...d };
            }
        });
        return {
            editedByIdentifier,
            newRows,
            deletedIds: Array.from(deletedIdentifiersRef.current),
            heldIds: Array.from(heldLocksRef.current),
        };
    };

    // Lay pending edits on top of freshly loaded truth rows (matched by internal_tanc_identifier).
    const mergeLocalEdits = (incoming, { editedByIdentifier, newRows, deletedIds }) => {
        const deleted = new Set(deletedIds);
        const appliedEdits = new Set();
        const merged = [];
        (incoming || []).forEach((row) => {
            const id = row.internal_tanc_identifier;
            if (id && deleted.has(id)) return;               // user deleted it
            if (id && editedByIdentifier[id]) {
                // Keep the user's edit on top. editedByIdentifier[id] (a full row copy)
                // already carries the original per-column `isEdited` OBJECT — do NOT set
                // `isEdited: true` (boolean), which would clobber that map and break both
                // cell-bold (isEdited.hasOwnProperty) and rowHasUnsavedWork (Object.keys),
                // leaving the value present but not bold / not submittable.
                merged.push({ ...row, ...editedByIdentifier[id] });
                appliedEdits.add(id);
            } else {
                merged.push(row);                                // incoming (may carry a peer's change)
            }
        });
        // Defensive: an edited row missing from the incoming truth (shouldn't
        // happen — locks stop a peer touching a row we hold) is preserved with its
        // original isEdited object intact.
        Object.keys(editedByIdentifier).forEach((id) => {
            if (!appliedEdits.has(id)) merged.push({ ...editedByIdentifier[id] })
        });
        newRows.forEach((r) => merged.push({ ...r, isNew: true }))
        return merged;
    };

    const syncAndRebaseLocalEdits = async () => {
        // 1. Snapshot pending local edits from the grid before we replace it.
        const snapshot = snapshotLocalEdits();
        const { editedByIdentifier, newRows } = snapshot;
        const deletedIds = new Set(snapshot.deletedIds);

        // 2. Fetch the latest truth.
        const sessionFileDetails = getS3PathAndFileName(sessionDetails?.s3Uri)
        const prefix = sessionFileDetails['path']
        configureTANCS3()
        const arr = sessionFileDetails['fileName'].split('.')
        const contentType = arr[arr.length - 1]
        let document
        try {
            document = await getDocumentFromVault(contentType, VAULT_SERVICES.TANC, { "file_path": `${prefix}${sessionFileDetails['fileName']}`, "service_id": `${sessionDetails.tancId}`, "app_id": `${sessionDetails.sessionId}` })
        } catch (e) {
            setAnnotationError('Sync failed'); return
        }
        await d3.csv(document?.fileUrl).then((incoming) => {
            // 3. Rebase local edits onto the incoming truth.
            const merged = mergeLocalEdits(incoming, snapshot);

            console.log(`[RowLock] sync-rebase session=${sessionDetails.sessionId} incoming=${(incoming || []).length} editsKept=${Object.keys(editedByIdentifier).length} newKept=${newRows.length} deletesKept=${deletedIds.size} at ${new Date().toISOString()}`)

            // 4. Apply to the grid, PRESERVING pending state (edit flags kept;
            // deletedIdentifiersRef intentionally NOT reset — unlike setViewOnTheGrid).
            merged.forEach((row, index) => { row._tancRowId = String(index) })
            totalRowsRef.current = merged.length
            nextRowIdRef.current = merged.length
            setDsInfo(merged)
            cleanData()
            setColumnDefinition(merged)
            setAnnotationLoader(null)
            setAnnotationError(null)
        }).catch(() => {
            setAnnotationError('Sync failed')
        })
    }


    function cleanData() {
        if (dsInfo == null || configurationMap == null) {
            return
        }
        dsInfo.forEach(row => {
            Object.keys(configurationMap).forEach(colName => {
                if ((configurationMap[colName]?.data_type === 'INTEGER' || configurationMap[colName]?.data_type === 'DECIMAL') && row[colName]) {
                    row[colName] = Number(row[colName]) || 0;
                }
            });
        });
    }

    function isEditorRole() {
        return selectedFilterOption?.extra?.role == 'EDITOR' || selectedFilterOption?.extra?.role == 'ADMIN' || selectedFilterOption?.extra?.role == 'REVIEWER'
    }
    function isEditable(colName) {
        if (!isEditorRole()) {
            return false
        }
        if (selectedFilterOption && selectedFilterOption?.value == 'All') {
            return true
        }
        try {
            let extras = JSON.parse(filters[selectedFilterOption?.value]?.extras)
            if (extras?.allEdit) {
                return true
            } else {
                let filter = JSON.parse(filters[selectedFilterOption?.value]?.information)
                for (const i in filter) {
                    if (filter[i].column_name == colName) {
                        return filter[i].can_edit
                    }
                }
                return false
            }
        } catch (e) {
            // console.log(e)
            return false
        }
    }

    const getMaxLengthForCellField = (tableDefinition, configurationMap) => {
        let length = 2000
        try {
            if (tableDefinition?.cellDataType == CellDataType.TEXT && (configurationMap[tableDefinition.field] || null)?.length) {
                length = configurationMap[tableDefinition.field].length
            }
        } catch (e) {

        }
        return length
    }

    const getDateString = (date) => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return null;
        }
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${date.getFullYear()}-${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}`;
    }

    const setColumnDefinition = (input) => {
        let data = dsInfo
        if (input) {
            data = input
        }
        let filterModel = {}
        if (data?.columns) {
            const definitions = []
            for (let i = 0; i < data?.columns?.length; i++) {
                let colName = data?.columns[i]
                if (colName === 'internal_tanc_identifier') {
                    definitions.push({
                        field: colName, headerName: colName, hide: true, editable: false,
                        sortable: false, filter: false, enableRowGroup: false,
                        suppressColumnsToolPanel: true, suppressFiltersToolPanel: true, lockVisible: true,
                    })
                    continue
                }
                let def = {
                    field: colName, headerName: colName, resizable: true, sortable: true, enableRowGroup: !inEditMode && formulaStoreRef.current.size === 0 && columnFormulaStoreRef.current.size === 0, editable: false,
                    filter: 'agMultiColumnFilter',
                    filterParams: {
                        closeOnApply: false,
                        defaultOption: 'contains',
                        trimInput: true,
                        caseSensitive: false,
                        suppressAndOrCondition: true,
                        filters: [
                            {
                                filter: 'agSetColumnFilter',
                                filterParams: {
                                    buttons: ['apply', 'clear'],
                                    suppressSelectAll: false,
                                    excelMode: 'windows',
                                    valueFormatter: (params) => {
                                        return params.value != null ? params.value?.toString() : '(Blank)';
                                    },
                                    applyValueImmediately: false
                                }
                            },
                        ]
                    },

                    cellClass: (params) => {
                        const colId = params?.column?.getColId();
                        const nodeId = params?.node?.id;
                        const key = nodeId && colId ? `${nodeId}-${colId}` : null;
                        const isTemporaryEdit = key ? temporaryEditFormulaCellsRef.current.has(key) : false;
                        const hasColumnFormula = colId && !isTemporaryEdit ? columnFormulaStoreRef.current.has(colId) : false;
                        const hasCellFormula = key ? formulaStoreRef.current.has(key) : false;
                        // Locked row = read-only: suppress the editable-cell bg (peer-held or
                        // my own in-flight row) so its grey lock indicator shows.
                        const internalTancId = params?.data?.internal_tanc_identifier;
                        const rowLocked = isRowLocked(internalTancId);
                        return [
                            (!rowLocked && params?.column?.colDef?.editable) ? 'editable-cell' : '',
                            params?.data?.isEdited?.hasOwnProperty(colName) ? 'edited-cell' : '',
                            (hasColumnFormula || hasCellFormula) ? 'formula-cell' : '',
                        ].filter(Boolean);
                    }
                }
                if (i == 0) {
                    def['checkboxSelection'] = true
                    def['headerCheckboxSelection'] = true
                }

                if (isEditable(colName) && inEditMode) {
                        // Locked row is read-only regardless of column rules (peer-held or my
                        // own in-flight row) until its TANCRowLock record is deleted.
                        def['editable'] = (params) => {
                            return !isRowLocked(params?.data?.internal_tanc_identifier);
                        };
                    } else {
                        def['editable'] = false
                    }
                if (configurationMap.hasOwnProperty(colName)) {
                    // check edit types
                    if (configurationMap[colName]?.edit_type == 'SELECT') {
                        def['cellEditor'] = 'agRichSelectCellEditor'
                        def['cellEditorParams'] = {
                            values: configurationMap[colName]?.field_tokens?.map(token => token.label) || [],
                        }
                    } else if (configurationMap[colName]?.edit_type == 'MULTI_SELECT') {
                        def['cellEditor'] = 'agRichSelectCellEditor'
                        def['cellEditorParams'] = {
                            values: configurationMap[colName]?.field_tokens?.map(token => token.label) || [],
                            multiSelect: true
                        }
                        def['valueFormatter'] = (params) => {
                            return Array.isArray(params.value) ? params.value.join(', ') : params.value;
                        };
                        def['valueParser'] = (params) => {
                            if (typeof params.newValue === 'string') {
                                return params.newValue.split(',').map(item => item.trim());
                            }
                            return params.newValue || [];
                        };
                        def['valueGetter'] = (params) => {
                            if (!params.data[colName]) return [];

                            // If it's already an array, return it
                            if (Array.isArray(params.data[colName])) {
                                return params.data[colName];
                            }

                            // If it's a string, convert to array
                            if (typeof params.data[colName] === 'string') {
                                return params.data[colName].split(',').map(item => item.trim());
                            }

                            return [];
                        };
                    } else {
                        def['cellEditor'] = 'agTextCellEditor'
                        def['cellDataType'] = CellDataType.TEXT
                        def['valueParser'] = (params) => {
                            return params.newValue || ''; // Ensure non-null value
                        };
                        def['valueFormatter'] = (params) => {
                            return params.value || '';
                        };


                        // check data types
                        if (configurationMap[colName]?.data_type == 'BOOLEAN') {
                            def['cellDataType'] = CellDataType.BOOLEAN
                        } else if (configurationMap[colName]?.data_type == 'DATE') {
                            def['cellDataType'] = CellDataType.DATE
                            def['valueFormatter'] = (params) => {
                                if(params.value instanceof Date){
                                    return getDateString(params.value)
                                }
                                return params.value
                            };
                            def['valueGetter'] = (params) => {
                                if(params.data?.[colName] instanceof Date){
                                    return getDateString(params.data?.[colName])
                                }
                                return params.data?.[colName]
                            };
                            def['valueParser'] = (params) => {
                                const parsedDate = new Date(params.newValue+ "T00:00:00");
                                if (parsedDate instanceof Date && !isNaN(parsedDate)) {
                                    return parsedDate
                                }
                                // Return previous value if invalid
                                return params.oldValue;
                            };

                        } else if (configurationMap[colName]?.data_type == 'INTEGER') {
                            delete def['cellEditor']
                            def['cellDataType'] = CellDataType.NUMBER
                            def['allowFormula'] = true
                            // Coerce stored value to Number (like CURRENCY) so oldValue matches
                            // the parser's number — else a no-op edit reads as a change.
                            def['valueGetter'] = (params) => {
                                const val = params.data?.[colName];
                                if (val === null || val === undefined || val === '') return val;
                                if (typeof val === 'string' && val.startsWith('=')) return val;
                                const num = Number(val);
                                return isNaN(num) ? val : num;
                            };
                            def['valueFormatter'] = (params) => {
                                if (typeof params.value === 'string' && params.value.startsWith('=')) return 0;
                                const value = Number(params.value);
                                if (isNaN(value)) return params.value;
                                return value.toFixed(0);
                            };
                            def['valueParser'] = (params) => {
                                if (typeof params.newValue === 'string' && params.newValue.startsWith('=')) return params.newValue;
                                const value = Number(params.newValue);
                                return isNaN(value) ? 0 : parseInt(value);
                            };
                        }else if (configurationMap[colName]?.data_type == 'DECIMAL') {
                            delete def['cellEditor']
                            def['cellDataType'] = CellDataType.NUMBER
                            def['allowFormula'] = true
                            def['valueFormatter'] = (params) => {
                                if (typeof params.value === 'string' && params.value.startsWith('=')) return 0;
                                const value = Number(params.value);
                                if (isNaN(value)) return params.value;
                                return value;
                            };
                            def['valueParser'] = (params) => {
                                if (typeof params.newValue === 'string' && params.newValue.startsWith('=')) return params.newValue;
                                const value = Number(params.newValue);
                                return isNaN(value) ? 0 : value;
                            };
                        }else if (configurationMap[colName]?.data_type == 'CURRENCY') {
                            delete def['cellEditor']
                            def['valueFormatter'] = (params) => {
                                if (typeof params.value === 'string' && params.value.startsWith('=')) return 0;
                                return currencyFormatter(params);
                            }
                            def['valueGetter'] = (params) => {
                                const val = params.data?.[colName];
                                if (typeof val === 'string' && val.startsWith('=')) return 0;
                                return Number(val) || 0;
                            }
                            def['valueParser'] = (params) => {
                                if (typeof params.newValue === 'string' && params.newValue.startsWith('=')) return params.newValue;
                                const cleanValue = params.newValue.toString().replace(/[^0-9.]/g, '');
                                const parsedValue = Number(cleanValue);
                                return isNaN(parsedValue) ? (params.oldValue || 0) : parsedValue;
                            }
                            def['cellDataType'] = CellDataType.NUMBER
                            def['allowFormula'] = true
                        }else if (configurationMap[colName]?.data_type == 'VARCHAR') {
                            def['cellDataType'] = CellDataType.TEXT
                            def['cellEditor'] = 'agLargeTextCellEditor'
                            def['cellEditorPopup'] = true
                            def['cellEditorParams'] = {
                                maxLength: getMaxLengthForCellField(def, configurationMap),
                                rows: 5,
                                cols: 50
                            }
                        } else {
                            def['cellEditorParams'] = {
                                maxLength: getMaxLengthForCellField(def, configurationMap),
                                onKeyDown: (event) => {
                                    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                                    if (!allowedKeys.includes(event.key) &&
                                        !/^[0-9.]$/.test(event.key)) {
                                        event.preventDefault();
                                    }
                                }
                            }
                        }
                    }

                    if (configurationMap[colName]?.data_type == 'DECIMAL'
                        || configurationMap[colName]?.data_type == 'INTEGER'
                        || configurationMap[colName]?.data_type == 'CURRENCY') {
                        def['filterParams']['filters'].push({
                            filter: 'agNumberColumnFilter',
                            filterParams: {
                                buttons: ['apply', 'clear'],
                            }
                        })
                    } else if (configurationMap[colName]?.data_type == 'DATE') {
                        def['filterParams']['filters'].push({
                            filter: 'agDateColumnFilter',
                            filterParams: {
                                buttons: ['apply', 'clear'],
                            }
                        })
                    } else {
                        def['filterParams']['filters'].push({
                            filter: 'agTextColumnFilter',
                            filterParams: {
                                closeOnApply: false,
                                buttons: ['apply', 'clear'],
                                filterOptions: [
                                    'equals',
                                    'notEqual',
                                    'contains',
                                    'notContains',
                                    'startsWith',
                                    'endsWith'
                                ]
                            }
                        })
                    }

                    // apply filters
                    if (selectedFilterOption
                        && selectedFilterOption.value
                        && filters.hasOwnProperty(selectedFilterOption.value)
                        && filters[selectedFilterOption.value]?.information) {
                        let filterConfig = JSON.parse(filters[selectedFilterOption.value]?.information)
                        for (const i in filterConfig) {
                            if (filterConfig[i].column_name == colName && filterConfig[i].field_tokens?.length > 0) {
                                const values = filterConfig[i].field_tokens?.map(item => item.label);
                                filterModel[colName] = {
                                    filterType: 'multi',
                                    // first element should always be a 'set' that is default applied filter. CANNOT BE NULL since this is referenced in the onFilterChanged to reset the filter if default is changed
                                    filterModels: [
                                        {
                                            filterType: 'set',
                                            values: values
                                        }
                                        // null is required. DONOT REMOVE. If filters are auto applied and for complex filter involving set and other filter types, auto apply fails. Even aggrid adds null for default behaviours. 
                                    ]
                                }
                            }
                        }
                    }

                    if (selectedFilterOption
                        && selectedFilterOption.value
                        && customFilters.hasOwnProperty(selectedFilterOption.value)
                        && customFilters[selectedFilterOption.value]?.information) {

                        const customFilterConfig = JSON.parse(customFilters[selectedFilterOption?.value]?.information)
                        let originalFilterId = JSON.parse(customFilters[selectedFilterOption?.value]?.extras).originalFilterId;
                        let originalFilter = filters[originalFilterId];
                        let originalFilterConfig, applicableOriginalFilter;
                        
                        if (originalFilter) {
                            originalFilterConfig = JSON.parse(filters[originalFilterId]?.information)
                            applicableOriginalFilter = originalFilterConfig.find(originalFilter => originalFilter.column_name == colName);
                        } 

                        if (customFilterConfig[colName] || applicableOriginalFilter) {
                            let filterModels = []

                            const values = applicableOriginalFilter?.field_tokens ? applicableOriginalFilter.field_tokens?.map(item => item.label) : [];
                            filterModels.push({
                                filterType: 'set',
                                values: values.length > 0 ? values : null,
                            })

                            if (customFilterConfig[colName]) {
                                const filters = customFilterConfig[colName].filterModels;
                                const setFilter = filters.find(filter => filter.filterType === 'set');
                                if (setFilter) {
                                    filterModels[0].values = setFilter.values;
                                }
                                const nonSetFilters = filters.filter(filter => filter.filterType != 'set')
                                filterModels = filterModels.concat(nonSetFilters);
                            }

                            filterModel[colName] = {
                                filterType: 'multi',
                                filterModels
                            }
                        }
                    }

                }
                definitions.push(def)
            }
            setTANCFilterModel(filterModel)
            setColumnsList(definitions)
        }
    }
    const onRegister = (rippleId) => {

    }

    const onDeRegister = (rippleId) => {

    }
    // Columns to persist on save/submit: all grid columns (incl. hidden identifier),
    // named explicitly so getDataAsCsv keeps the identifier; `_tancRowId` is excluded.
    const getPersistColumnKeys = () => columnsList?.map(c => c.field)

    // Shared by the FLEXICO save and FRISCO submit row-lock paths: brand-new rows have
    // no identifier; mint + hold a UUID so each exports as a claimed, appendable row.
    const mintIdsForNewRows = () => {
        gridRef.current?.api?.forEachNode((node) => {
            const d = node?.data;
            if (d?.isNew && !d.internal_tanc_identifier) {
                d.internal_tanc_identifier = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
                heldLocksRef.current.add(d.internal_tanc_identifier);
            }
        });
    };

    // Every row with unsaved work (edited / new / pending delete) is part of the save, even if
    // its lock was released mid-edit; the SUBMITTED write re-claims it (fails if a peer holds it).
    const claimRowsWithUnsavedWork = () => {
        gridRef.current?.api?.forEachNode((node) => {
            const id = node?.data?.internal_tanc_identifier;
            if (id && rowHasUnsavedWork(node.data)) heldLocksRef.current.add(id);
        });
        deletedIdentifiersRef.current.forEach(id => heldLocksRef.current.add(id));
    };

    // Shared row-lock submit trigger (FLEXICO save + FRISCO submit): flip the held rows
    // EDITING→SUBMITTED (carry delta_key) and kick off the server-side rebase
    // orchestrator (which, for FRISCO, tails into the ingest). Marks this client the
    // saver; resets that flag on failure so a peer's success can't wrongly exit us.
    const triggerRowLockRebase = async (deltaKey, comment) => {
        savingRef.current = true;
        claimRowsWithUnsavedWork();
        try {
            await transitionHeldLocks('SUBMITTED', `${deltaKey}`, comment);
        } catch (e) {
            savingRef.current = false;
            setAnnotationError("Saving failed — another user now holds a row you changed (greyed). Nothing was saved; cancel and redo your changes.");
            return;
        }
        tancSubmitRebase(sessionDetails.sessionId, function onSuccess(res) {
        }, function onFailure(err) {
            savingRef.current = false;
            setAnnotationError("Saving failed");
        })
    };

    const saveAnnotated = async() => {
        resetSaveRequestModal()
        setAnnotationLoader("Saving the data..")
        if (hasUnsavedFormula) {
            saveFormulas();
        }
        // Force refresh to ensure formula cells have latest computed values before export
        if (gridRef.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
        }
        const sessionFileDetails = getS3PathAndFileName(sessionDetails?.s3Uri)
        let prefix = sessionFileDetails['path']
        prefix = `tmp/${prefix}`
        const [name, ext] = sessionFileDetails['fileName'].split('.');
        const new_filename = `${name}_${Math.floor(Date.now()/1000)}.${ext}`;

        let delta_filename = `delta_${new_filename}`
        if (resubmitTimestamp?.['versionData']?.version) {
            delta_filename = `resubmitted_${resubmitTimestamp?.['versionData']?.lastUpdatedTimestamp}_${resubmitTimestamp?.['versionData']?.version}.csv`
        }
        setResubmitTimestamp(null);
        // Brand-new rows have no identifier — mint one before building the delta.
        if (rowLockingEnabled) {
            mintIdsForNewRows();
        }
        let dataAltered = gridRef.current?.api.getDataAsCsv({
            'exportedRows': 'all',
            columnKeys: getPersistColumnKeys(),
            shouldRowBeSkipped: (params) => !(params.node.data.isEdited || params.node.data.isNew),
            processCellCallback: (params) => {
                return parseExportValue(params);
            },
        })
        configureTANCS3()
        // let putResp = await Storage.vault.put(delta_filename, dataAltered, {
        //                 tagging: `userId=${userId}&tanc_id:${sessionDetails.tancId}&session_id:${sessionDetails.sessionId}`,
        //                 level: annotationLevel,
        //                 customPrefix: { public: prefix },
        //                 acl: 'bucket-owner-full-control'
        //             })
        
        let putResp = await uploadDocumentToVault(ext, VAULT_SERVICES.TANC, {"file_path": `${prefix}/${delta_filename}`, "tagging": `userId=${userId}&tanc_id:${sessionDetails.tancId}&session_id:${sessionDetails.sessionId}`, "service_id":`${sessionDetails.tancId}`, "app_id":`${sessionDetails.sessionId}`}, dataAltered)
        
        let data = gridRef.current?.api.getDataAsCsv({
            'exportedRows': 'all', columnKeys: getPersistColumnKeys(), processCellCallback: (params) => {
                // Access raw data directly from node.data
                return parseExportValue(params);
            },
        })
        configureTANCS3()
        uploadDocumentToVault(ext, VAULT_SERVICES.TANC, {"file_path": `${prefix}${new_filename}`, "service_id":`${sessionDetails.tancId}`, "app_id":`${sessionDetails.sessionId}`}, data)
        // Storage.put(new_filename, data, {
        //     level: annotationLevel,
        //     customPrefix: { public: prefix },
        // })
            .then(async (result) => {
                setTimeout(() => {
                    setAnnotationSaveSuccessMessage(null)
                }, 1000);
                // Mark this client as the saver so the broadcast SYNC_STATUS
                // 'success' resets edit state here only (not for other editors).
                savingRef.current = true;
                // [RowLock] Trace the delta handed to the server-side rebase:
                // edited + new rows and the identifiers being deleted.
                let _editedRows = 0, _newRows = 0;
                gridRef.current?.api?.forEachNode(n => {
                    if (n?.data?.isNew) _newRows++;
                    else if (n?.data?.isEdited) _editedRows++;
                });
                console.log(`[RowLock] save→rebase session=${sessionDetails.sessionId} editedRows=${_editedRows} newRows=${_newRows} deletedIds=${deletedIdentifiersRef.current.size} deltaKey=${putResp.objectKey} tempKey=${result.objectKey} at ${new Date().toISOString()}`);
                if (rebaseSaveEnabled) {
                    // FLEXICO rebase save: stage rows SUBMITTED + trigger the orchestrator
                    // (shared with the FRISCO submit path).
                    await triggerRowLockRebase(putResp.objectKey, saveRequestComment);
                } else {
                    // Legacy full-session-lock save. On failure reset savingRef, else
                    // the next peer's success would wrongly exit this client from editing.
                    tancSave(sessionDetails.sessionId, saveRequestComment, `${result.objectKey}`, 'UPDATE', 'ABC', `${putResp.objectKey}`, function onSuccess(res){

                    }, function onFailure(err){
                        savingRef.current = false;
                        setAnnotationError("Saving failed");
                    }, Array.from(deletedIdentifiersRef.current))
                }
            })
            .catch(err => {
                // Reset the saver flag on failure, else the next peer's success
                // broadcast would wrongly exit this client and drop its work.
                savingRef.current = false;
                setAnnotationError("Saving failed");
            });
    }

    const parseExportValue = (params) => {
        const rawValue = params.node.data[params.column.colDef.field];
        const colId = params.column.colDef.field;
        // If the cell has a column formula or cell-level formula, export the displayed value
        if (columnFormulaStoreRef.current.has(colId) || formulaStoreRef.current.has(`${params.node.id}-${colId}`) || (typeof rawValue === 'string' && rawValue.startsWith('='))) {
            return params.value != null ? params.value : 0;
        }
        if (rawValue instanceof Date) {
            return getDateString(rawValue)
        }
        return rawValue;
    }
    // Row-locking Submit (FLEXICO + opted-in FRISCO): the SERVER-SIDE rebase path.
    // Stage only the DELTA (changed/new rows), flip held rows SUBMITTED (carrying the
    // delta_key), and trigger the orchestrator, which rebases the delta onto the LATEST
    // truth. For FRISCO the orchestrator's tail then hands the merged result to the
    // EXISTING FRISCO ingest. This REPLACES the whole-file onSubmit/friscoSubmit path,
    // so a row-locking submit cannot bypass the orchestrator. No whole-session lock;
    // held rows are NOT released here — the orchestrator releases them after the truth
    // is updated (clear_cache). Mirrors saveAnnotated's rebase branch.
    // FRISCO row-lock submit — STEP 1 of the wizard (Upload → Review → Submit).
    // Stages the delta + flips the held rows EDITING→SUBMITTED (carry delta_key), then
    // ADVANCES the FRISCO wizard to the Review step via props.onSubmit — it does NOT
    // release the locks and does NOT fire the orchestrator. The actual submit effect
    // (tancSubmitRebase → orchestrator → rebase → ingest) is triggered by the wizard's
    // STEP 3 submit (FriscoUploadComponent.onSubmitClick, gated on rowLockingEnabled),
    // so the full Upload→Review→Submit flow is preserved and the rows stay LOCKED the
    // whole way (EDITING→SUBMITTED→PROCESSING→PENDING_FRISCO_INGEST) until clear_cache
    // releases them after the truth file is updated. Staging here (not at Step 3) is
    // deliberate: the grid is guaranteed mounted now, so the delta is captured before
    // the wizard advances past the inline-edit view.
    const submitViaRowLockRebase = async () => {
        setAnnotationLoader("Submitting...")
        if (hasUnsavedFormula) { saveFormulas(); }
        if (gridRef.current?.api) { gridRef.current.api.refreshCells({ force: true }); }
        const sessionFileDetails = getS3PathAndFileName(sessionDetails?.s3Uri)
        const prefix = `tmp/${sessionFileDetails['path']}`
        const [name, ext] = sessionFileDetails['fileName'].split('.')
        const new_filename = `${name}_${Math.floor(Date.now() / 1000)}.${ext}`
        const delta_filename = `delta_${new_filename}`
        mintIdsForNewRows();  // shared with FLEXICO save
        claimRowsWithUnsavedWork();
        const dataAltered = gridRef.current?.api.getDataAsCsv({
            'exportedRows': 'all',
            columnKeys: getPersistColumnKeys(),
            shouldRowBeSkipped: (params) => !(params.node.data.isEdited || params.node.data.isNew),
            processCellCallback: (params) => parseExportValue(params),
        })
        // Full grid for the Review step (props.onSubmit expects whole-file first arg).
        const dataFull = gridRef.current?.api.getDataAsCsv({
            'exportedRows': 'all',
            columnKeys: getPersistColumnKeys(),
            processCellCallback: (params) => parseExportValue(params),
        })
        setResubmitTimestamp(null);
        configureTANCS3()
        try {
            const putResp = await uploadDocumentToVault(ext, VAULT_SERVICES.TANC, { "file_path": `${prefix}/${delta_filename}`, "tagging": `userId=${userId}&tanc_id:${sessionDetails.tancId}&session_id:${sessionDetails.sessionId}`, "service_id": `${sessionDetails.tancId}`, "app_id": `${sessionDetails.sessionId}` }, dataAltered)
            // Step 1 STAGES ONLY: upload the delta to S3 and capture which rows/deletions
            // to submit. The rows stay EDITING and TANCRowLock is NOT written to SUBMITTED
            // here — if the user changes their mind at Review, nothing was committed. The
            // SUBMITTED flip + orchestrator trigger happen at STEP 3
            // (FriscoUploadComponent.onSubmitClick) once the user actually confirms. Grab
            // heldIds/deletedIds now because the grid unmounts when the wizard advances.
            const rowLockSubmit = {
                deltaKey: putResp.objectKey,
                heldIds: Array.from(heldLocksRef.current),
                deletedIds: Array.from(deletedIdentifiersRef.current),
                comment: 'FRISCO row-lock submit',
                draft: snapshotLocalEdits(),  // restored if the user goes Back to Step 1
            };
            console.log(`[RowLock] submit→STAGE ONLY (rows stay EDITING; SUBMITTED deferred to step 3) session=${sessionDetails.sessionId} deltaKey=${putResp.objectKey} heldRows=${rowLockSubmit.heldIds.length} at ${new Date().toISOString()}`);
            // Advance the FRISCO wizard to the Review step, handing the row-lock submit
            // info to the parent so Step 3 can commit it. NO transitionHeldLocks, NO release.
            if (props?.onSubmit) { props.onSubmit(dataFull, dataAltered, 'updated', rowLockSubmit); }
            if (props?.onSubmitAltered) { props.onSubmitAltered(dataAltered, 'updated', rowLockSubmit); }
            setAnnotationLoader(null);
        } catch (e) {
            savingRef.current = false;
            setAnnotationLoader(null);
            setAnnotationError("Submit failed");
        }
    }

    const submitAnnotated = () => {
        // Row-locking sessions submit via the server-side rebase (delta → orchestrator),
        // NOT the whole-file onSubmit — this is what makes the orchestrator the sole
        // path for grid-edit submits and prevents the whole-page overwrite from
        // bypassing row locking. Legacy (non-row-locking) FRISCO keeps the old flow.
        if (rowLockingEnabled) {
            submitViaRowLockRebase();
            return;
        }
        if (hasUnsavedFormula) {
            saveFormulas();
        }
        // Force refresh to ensure formula cells have latest computed values before export
        if (gridRef.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
        }
        let dataAltered = gridRef.current?.api.getDataAsCsv({
            'exportedRows': 'all',
            columnKeys: getPersistColumnKeys(),
            shouldRowBeSkipped: (params) => !(params.node.data.isEdited || params.node.data.isNew),
            processCellCallback: (params) => {
                return parseExportValue(params);
            },
        })
        let fileName = 'updated'
        if (resubmitTimestamp?.['versionData']?.version) {
            fileName = `resubmitted_${resubmitTimestamp?.['versionData']?.lastUpdatedTimestamp}_${resubmitTimestamp?.['versionData']?.version}`
        }
        setResubmitTimestamp(null);
        if (props?.onSubmit) {
            let data = gridRef.current?.api.getDataAsCsv({
                'exportedRows': 'all', columnKeys: getPersistColumnKeys(), processCellCallback: (params) => {
                    return parseExportValue(params);
                },
            })
            props?.onSubmit(data, dataAltered, fileName)
        }

        if (props?.onSubmitAltered) {
            props?.onSubmitAltered(dataAltered, fileName)
        }
        // Submit RELEASEs this user's rows (delta already staged on save), letting
        // the orchestrator's all-RELEASE gate fire the deferred FRISCO push.
        releaseHeldLocks();
    }

    const nextRowIdRef = useRef(0);

    // Identifiers of rows deleted this session, kept so the rebase-on-save can
    // replay the delete. Cleared on every fresh load (setViewOnTheGrid).
    const deletedIdentifiersRef = useRef(new Set());

    // Per-row locks: `identifier -> { lockedBy, state }` for rows that must render
    // read-only/greyed. Populated from the TANCRowLock table (loadRowLockSnapshot)
    // and live ROW_LOCK_STATUS pushes.
    const [rowLocks, setRowLocks] = useState({});
    // Mirror of rowLocks for the column `editable`/getRowStyle closures, which
    // are created once and would otherwise read a stale rowLocks snapshot.
    const rowLocksRef = useRef({});

    // Committed/in-flight states: a row here stays locked for EVERYONE (incl. the
    // submitter) until its TANCRowLock record is DELETED (= truth updated).
    const COMMITTED_LOCK_STATES = new Set(['SUBMITTED', 'PROCESSING', 'PENDING_FRISCO_INGEST']);

    // Locked/read-only for me whenever a lock record exists, EXCEPT my own EDITING row.
    const isRowLocked = (internalTancId) => {
        const l = internalTancId ? rowLocksRef.current[internalTancId] : null;
        if (!l) return false;
        return l.lockedBy !== userId || COMMITTED_LOCK_STATES.has(l.state);
    };
    // The alias holding the row (for the "Locked by X" tooltip), or null.
    const rowLockOwner = (internalTancId) => (internalTancId ? rowLocksRef.current[internalTancId]?.lockedBy : null) || null;
    // Whether a PEER (not me) holds the row — used where we must not touch a peer's row.
    const peerHoldsRow = (internalTancId) => {
        const owner = rowLockOwner(internalTancId);
        return Boolean(owner && owner !== userId);
    };

    // True only on the client that initiated the current save, so the broadcast
    // SYNC_STATUS 'success' resets edit state for the saver alone.
    const savingRef = useRef(false);
    // Guards the one-time "stamp identifiers on load" retry so we never loop.
    const identifierEnsuredRef = useRef(false);
    // Rows this tab holds locks on: dedupes the tancRowLockEvent mutation and
    // tracks exactly what to release on cancel / save-complete.
    const heldLocksRef = useRef(new Set());

    // Data of the last-focused row, so onCellFocused can tell when the user
    // leaves a row (vs. moving between cells in it); kept as the live object.
    const prevFocusedRowRef = useRef(null);

    // Per-row locking is ON for FLEXICO always, and for FRISCO only when the
    // backend has opted this view in. The backend is the single source of truth:
    // it sets tancDetails.rowLockingEnabled via Constants.is_row_locking_enabled
    // (which consults the FRISCO rollout allowlist). We do NOT hardcode 'FRISCO'
    // here — that would flip every FRISCO view on at once and bypass the kill
    // switch. Fallback keeps FLEXICO on if the flag isn't present in the payload.
    const rowLockingEnabled = props?.tancDetails?.rowLockingEnabled === true
        || props?.tancDetails?.association_id_type === 'FLEXICO';

    // Two DIFFERENT capabilities, deliberately separated:
    //  - rowLockingEnabled (above): per-row COORDINATION — locks, presence,
    //    heartbeat, greying peer rows, per-row edit mode. FLEXICO + opted-in FRISCO.
    //  - rebaseSaveEnabled (here): the FLEXICO SAVE model — a per-row delta written
    //    to the S3 truth and rebased (Save button -> saveAnnotated -> tancSubmitRebase).
    //    FLEXICO ONLY. FRISCO must NOT use this: FRISCO's persistence is a whole-file
    //    re-ingest through validation -> FAST -> Redshift, driven by its own Submit
    //    (submitAnnotated -> props.onSubmit -> tancSubmit). So for FRISCO we enable
    //    coordination locks WITHOUT the S3-rebase save.
    const rebaseSaveEnabled = props?.tancDetails?.association_id_type === 'FLEXICO';

    // Unified "in edit mode" predicate: row-locking sessions use local per-row
    // editing (isEditing); legacy sessions use the full-session lock held by this user.
    const inEditMode = rowLockingEnabled
        ? isEditing
        : (sessionDetails?.isLocked && sessionDetails?.lockedBy == userId);

    // One row per call — a conditional single-item write to TANCRowLock keyed on
    // internal_tanc_identifier. Alias is server-derived, never sent.
    const sendRowLockEvent = (internalTancId, modificationType, onFailure, deltaKey, pendingDelete, saveComment, onSuccess) => {
        if (!rowLockingEnabled || !internalTancId || !sessionDetails?.sessionId) {
            // Testing aid: surface WHY a lock event was not sent — most commonly
            // rowLockingEnabled=false because association_id_type is not a
            // row-locking type (FLEXICO / FRISCO).
            console.log(`[RowLock] SKIP ${modificationType} row=${internalTancId} — rowLockingEnabled=${rowLockingEnabled} assocType=${props?.tancDetails?.association_id_type} sessionId=${sessionDetails?.sessionId}`);
            return;
        }
        console.log(`[RowLock] send ${modificationType} row=${internalTancId} at ${new Date().toISOString()}`);
        // Return the write promise so callers can await the row state landing.
        const writePromise = tancRowLockEvent(
            sessionDetails.sessionId,
            String(sessionDetails.tancId || ''),
            internalTancId,
            modificationType,
            deltaKey,
            pendingDelete,
            onSuccess,
            onFailure,
            saveComment,
        );
        // Publish the peer notification immediately so other UIs update without
        // waiting on the DynamoDB stream (broadcaster skips this write, no dupe).
        const action = modificationType === 'RELEASE' ? 'RELEASED' : 'LOCKED';
        const payload = { type: 'ROW_LOCK_STATUS', action, internal_tanc_identifier: internalTancId, lockedBy: userId };
        if (action === 'LOCKED') payload.tanc_lock_state = modificationType;
        tancUpdateSubscribers(sessionDetails.sessionId, payload);
        return writePromise;
    };

    // A conditional acquire lost the race (a peer holds the row). Drop the
    // optimistic hold, cancel the edit, re-sync the true owner, and warn.
    const onAcquireLost = (internalTancId, error) => {
        console.log(`[RowLock] acquire LOST row=${internalTancId} session=${sessionDetails?.sessionId} at ${new Date().toISOString()}`);
        heldLocksRef.current.delete(internalTancId);
        const api = gridRef.current?.api;
        const editingLostRow = (api?.getEditingCells?.() || []).some(c =>
            api.getDisplayedRowAtIndex(c.rowIndex)?.data?.internal_tanc_identifier === internalTancId);
        if (editingLostRow) api.stopEditing(true);
        // Grey the row immediately (priority) so the lock shows even before the
        // reconciling query returns / if the peer's broadcast was missed.
        setRowLocks(prev => {
            const existing = prev[internalTancId];
            const owner = (existing?.lockedBy && existing.lockedBy != userId) ? existing.lockedBy : 'another user';
            const next = { ...prev, [internalTancId]: { lockedBy: owner, state: 'EDITING' } };
            rowLocksRef.current = next;
            return next;
        });
        gridRef.current?.api?.redrawRows();
        // Reconcile the true owner. Preserve the grey through a stale read ONLY on a
        // genuine conditional (peer) failure; a transient error lets the query decide.
        const isPeerLock = /ConditionalCheckFailed/i.test(JSON.stringify(error || ''));
        loadRowLockSnapshot(isPeerLock ? internalTancId : undefined);
        setFlashMessages([{
            type: 'warning',
            dismissible: true,
            dismissLabel: 'Dismiss',
            onDismiss: () => setFlashMessages([]),
            header: 'Row locked by another user',
            content: 'Someone else is editing this row — your change was not applied.',
            id: 'row_lock_lost',
        }]);
    };

    const acquireRowLock = (internalTancId) => {
        if (!internalTancId) {
            console.log('[RowLock] acquireRowLock skip: no internal_tanc_identifier');
            return;
        }
        if (heldLocksRef.current.has(internalTancId)) {
            console.log(`[RowLock] acquireRowLock skip: already held row=${internalTancId}`);
            return;
        }
        heldLocksRef.current.add(internalTancId);
        sendRowLockEvent(internalTancId, 'EDITING', (err) => onAcquireLost(internalTancId, err));
    };

    // Get the row's identifier, minting one in place if absent so it's lockable.
    // CAVEAT: a minted id on a legacy row with no base-truth id appends as a duplicate.
    const ensureRowIdentifier = (data) => {
        if (!data) return null;
        if (!data.internal_tanc_identifier) {
            data.internal_tanc_identifier =
                (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            console.log(`[RowLock] minted identifier ${data.internal_tanc_identifier} for a row with none`);
        }
        return data.internal_tanc_identifier;
    };

    // Cancel path: free every held row (one RELEASE per row) so peers ungrey them.
    const releaseHeldLocks = () => {
        if (!rowLockingEnabled || heldLocksRef.current.size === 0 || !sessionDetails?.sessionId) return;
        const rowsToRelease = Array.from(heldLocksRef.current);
        console.log(`[RowLock] releasing ALL ${rowsToRelease.length} held row(s) session=${sessionDetails.sessionId}`);
        heldLocksRef.current.clear();
        rowsToRelease.forEach(internalTancId => sendRowLockEvent(internalTancId, 'RELEASE'));
    };

    // Release one held row: delete it from TANCRowLock and broadcast RELEASED so peers ungrey it.
    const releaseRowLock = (internalTancId) => {
        if (!rowLockingEnabled || !sessionDetails?.sessionId) return;
        if (!internalTancId || !heldLocksRef.current.has(internalTancId)) return;
        heldLocksRef.current.delete(internalTancId);
        sendRowLockEvent(internalTancId, 'RELEASE');
    };

    // Same check by row id (live grid data), for code that only has the id.
    const rowIdHasUnsavedWork = (internalTancId) =>
        deletedIdentifiersRef.current.has(internalTancId)
        || rowHasUnsavedWork(gridRef.current?.api?.getRowNode(internalTancId)?.data);

    // True if the row has unsaved work that must keep its lock: an edit, a new row, or a pending delete.
    const rowHasUnsavedWork = (data) => {
        if (!data) return false;
        const isNewRow = Boolean(data.isNew);
        const hasEditedCells = data.isEdited && Object.keys(data.isEdited).length > 0;
        const isPendingDelete = data.internal_tanc_identifier
            && deletedIdentifiersRef.current.has(data.internal_tanc_identifier);
        return Boolean(isNewRow || hasEditedCells || isPendingDelete);
    };

    // Re-announce held locks with a new state (EDITING -> SUBMITTED on save) without
    // releasing them. Resolves once every write has landed.
    const transitionHeldLocks = async (modificationType, deltaKey, saveComment) => {
        if (!rowLockingEnabled || heldLocksRef.current.size === 0 || !sessionDetails?.sessionId) return;
        const ids = Array.from(heldLocksRef.current);
        console.log(`[RowLock] transitioning ${ids.length} held row(s) → ${modificationType} session=${sessionDetails.sessionId}`);
        // Wait for each write to land (or fail); on SUBMITTED, mark deleted held rows pendingDelete.
        const results = await Promise.allSettled(ids.map(internalTancId => new Promise((resolve, reject) => {
            const pendingDelete = modificationType === 'SUBMITTED' && deletedIdentifiersRef.current.has(internalTancId);
            sendRowLockEvent(internalTancId, modificationType, (err) => reject(err || new Error('row lock write failed')),
                deltaKey, pendingDelete, saveComment, resolve);
        })));
        const failed = ids.filter((_, i) => results[i].status === 'rejected');
        if (failed.length === 0) return;
        ids.forEach((id, i) => { if (results[i].status === 'rejected') onAcquireLost(id, results[i].reason); });
        // Partial flip: put the flipped rows back to EDITING so no part of this save is applied.
        if (modificationType === 'SUBMITTED') {
            ids.filter((_, i) => results[i].status === 'fulfilled').forEach(id => sendRowLockEvent(id, 'EDITING'));
        }
        console.log(`[RowLock] ${failed.length} row(s) failed → ${modificationType}: ${failed.join(',')}`);
        throw new Error(`could not lock ${failed.length} row(s) for save`);
    };

    // Once per page load: free my EDITING locks left from an earlier visit (their edits aren't
    // in this grid). Skips rows this tab holds or is restoring from the wizard draft.
    const leftoverReleaseDoneRef = useRef(false);
    const releaseLeftoverOwnEditingLocks = (locks) => {
        if (leftoverReleaseDoneRef.current) return;
        leftoverReleaseDoneRef.current = true;
        const leftovers = (locks || []).filter(l =>
            l?.internal_tanc_identifier && l.lockedBy == userId && l.tanc_lock_state === 'EDITING'
            && !heldLocksRef.current.has(l.internal_tanc_identifier)
            && !(props?.pendingRowLockDraft?.heldIds || []).includes(l.internal_tanc_identifier));
        if (leftovers.length === 0) return;
        console.log(`[RowLock] releasing ${leftovers.length} leftover own EDITING lock(s) session=${sessionDetails.sessionId}`);
        leftovers.forEach(l => sendRowLockEvent(l.internal_tanc_identifier, 'RELEASE'));
    };

    // On load, read the current lock set directly from TANCRowLock so peer-held
    // rows grey immediately; the subscription then keeps it live.
    const loadRowLockSnapshot = async (preserveId) => {
        if (!rowLockingEnabled || !sessionDetails?.sessionId) return;
        try {
            const locks = await getRowLocksForSession(sessionDetails.sessionId);
            const next = {};
            (locks || []).forEach(l => {
                if (!l?.internal_tanc_identifier || !l?.lockedBy) return;
                // Locked if a peer holds it (any state) OR it's my own committed/in-flight
                // row; my own EDITING row stays editable (skipped).
                if (l.lockedBy != userId || COMMITTED_LOCK_STATES.has(l.tanc_lock_state)) {
                    next[l.internal_tanc_identifier] = { lockedBy: l.lockedBy, state: l.tanc_lock_state };
                }
            });
            console.log(`[RowLock] load snapshot session=${sessionDetails.sessionId}: ${(locks || []).length} total lock(s), ${Object.keys(next).length} rendered locked`);
            releaseLeftoverOwnEditingLocks(locks);
            // Keep a just-failed acquire greyed if this eventually-consistent read
            // hasn't caught up to the peer's lock yet.
            if (preserveId && !next[preserveId]) {
                next[preserveId] = rowLocksRef.current[preserveId] || { lockedBy: 'another user', state: 'EDITING' };
            }
            rowLocksRef.current = next;
            setRowLocks(next);
            gridRef.current?.api?.redrawRows();
        } catch (e) {
            console.log(`[RowLock] load snapshot failed: ${e}`);
        }
    };

    // True if this user has uncommitted work a blind RELOAD would wipe. Reads live
    // grid state via the API so it's correct from the AppSync subscription callback.
    const hasLocalEdits = () => {
        if (heldLocksRef.current.size > 0) return true;
        if (deletedIdentifiersRef.current.size > 0) return true;
        let edited = false;
        gridRef.current?.api?.forEachNode(node => {
            if (node?.data?.isEdited || node?.data?.isNew) edited = true;
        });
        return edited;
    };

    // Refresh / close / navigate-away with unsaved row-lock edits: show the browser's
    // "Leave site?" prompt (edits live only in this page). Ref avoids a stale closure.
    const hasLocalEditsRef = useRef(hasLocalEdits);
    hasLocalEditsRef.current = hasLocalEdits;
    useEffect(() => {
        if (!rowLockingEnabled) return;
        const onBeforeUnload = (e) => {
            if (!hasLocalEditsRef.current()) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [rowLockingEnabled]);

    // Timestamp of the user's last grid interaction (via markActivity). The
    // heartbeat only refreshes locks while recent, so idle tabs let locks lapse.
    const lastActivityRef = useRef(Date.now());
    // How long after the last interaction we keep heartbeating; past this the
    // lock frees ~LOCK_TTL (10 min) later.
    const HEARTBEAT_ACTIVITY_WINDOW_MS = 5 * 60 * 1000;
    const markActivity = () => { lastActivityRef.current = Date.now(); };

    // Heartbeat: re-assert held locks every 30s so their TTL (10 min) never lapses
    // while actively editing. Gated on recent activity so idle tabs don't refresh.
    useEffect(() => {
        const interval = setInterval(() => {
            const active = (Date.now() - lastActivityRef.current) < HEARTBEAT_ACTIVITY_WINDOW_MS;
            // Skip while saving: held rows are SUBMITTED and owned by the orchestrator;
            // re-asserting EDITING would flip them back out of its reach.
            if (rowLockingEnabled && !savingRef.current && heldLocksRef.current.size > 0 && sessionDetails?.sessionId) {
                // Rows with unsaved changes stay locked while the tab is open; idle unedited rows lapse.
                const ids = Array.from(heldLocksRef.current).filter(id => active || rowIdHasUnsavedWork(id));
                ids.forEach(id => sendRowLockEvent(id, 'EDITING', (err) => onAcquireLost(id, err)));
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [sessionDetails?.sessionId, sessionDetails?.tancId, rowLockingEnabled]);

    const addRowToAnnotation = (data = {}) => {
        // No lock for brand-new rows: they have no identifier and no peer sees them
        // yet. They become lockable (EDITING) once saved and reloaded with an id.
        let copy = Object.assign({}, data)
        copy['isNew'] = true
        const newId = nextRowIdRef.current;
        nextRowIdRef.current = nextRowIdRef.current + 1;
        copy['_tancRowId'] = String(newId);
        const newData = [copy, ...dsInfo];
        totalRowsRef.current = newData.length;
        setDsInfo(newData)
        setUndoEditCount(undoEditCount + 1)
    }

    const delRowToAnnotation = () => {
        // Never delete a locked row (peer-held → rebase would clobber their edit; or my own
        // in-flight row awaiting the truth update).
        const rowIdsToDelete = new Set();
        // Capture identifiers so the delete survives to save time. Unpersisted new
        // rows have none — they just vanish from the grid, no server delete needed.
        multiSelectedRow.forEach(row => {
            const internalTancId = row.data.internal_tanc_identifier
            if (isRowLocked(internalTancId)) {
                return  // locked (peer-held or my own in-flight) — leave it, don't delete
            }
            rowIdsToDelete.add(row.data._tancRowId)
            if (internalTancId) {
                deletedIdentifiersRef.current.add(internalTancId)
                // Lock the row as EDITING so peers see it held until this user's save
                // rebases the delete in; released with the rest on save/cancel/TTL.
                heldLocksRef.current.add(internalTancId)
                sendRowLockEvent(internalTancId, 'EDITING')
            }
        });
        const updatedData = dsInfo.filter(row => !rowIdsToDelete.has(row._tancRowId));
        totalRowsRef.current = updatedData.length;
        setDsInfo(updatedData)
        setUndoEditCount(undoEditCount + 1)
    }

    const exportAnnotated = (args = {}) => {
        const sessionFileDetails = getS3PathAndFileName(sessionDetails?.s3Uri)
        let csv = gridRef.current?.api.exportDataAsCsv({
            'fileName': sessionFileDetails['fileName'],
            'exportedRows': args.filtered ? 'filteredAndSorted' : 'all',
            'onlySelected': args.selectedOnly,
            columnKeys: columnsList?.filter(c => c.field !== '_tancRowId' && c.field !== 'internal_tanc_identifier').map(c => c.field),
            processCellCallback: (params) => {
                // Access raw data directly from node.data
                if (params.node.group) {
                    // Access grouped value
                    return params.value;
                } else {
                    return parseExportValue(params);
                }

            }
        });
        // gridRef.current?.api.exportDataAsExcel()
    }

    function currencyFormatter(params) {
        if (isNumberAfterParsed(params.value)) {
            return "$" + formatFloatNumber(params.value);
        } else {
            return '-'
        }

    }
    function formatNumber(number) {
        return Math.floor(number).toLocaleString();
    }

    function formatFloatNumber(number) {
        return number.toLocaleString();
    }

    const onCellValueChanged = useCallback((params) => {
        markActivity();
        const undoSize = params.api.getCurrentUndoSize();
        setUndoEditCount(undoSize)

        const colId = params.column.colId
        const editedCols = params.data.isEdited || {}
        // Compare to the cell's ORIGINAL value by value, not type ("1.50" vs 1.5, "2024-01-01" vs Date).
        const original = editedCols.hasOwnProperty(colId) ? editedCols[colId]?.oldValue : params.oldValue
        if (isSameCellValue(original, params.newValue)) {
            delete editedCols[colId]  // no change, or changed back to the original
        } else {
            editedCols[colId] = { oldValue: original, newValue: params.newValue }
        }
        if (Object.keys(editedCols).length > 0) {
            params.data.isEdited = editedCols;
        } else {
            delete params.data.isEdited;
        }
        params.api.refreshCells();
    }, []);

    // The instant a user enters a cell in edit mode, claim that row. Rows with no
    // identifier yet (unsaved new rows) are skipped until saved.
    const onCellEditingStarted = useCallback((params) => {
        markActivity();
        if (!rowLockingEnabled) {
            console.log(`[RowLock] onCellEditingStarted skip: rowLockingEnabled=false assocType=${props?.tancDetails?.association_id_type}`);
            return;
        }
        const internalTancId = ensureRowIdentifier(params?.data);
        if (!internalTancId) {
            console.log('[RowLock] onCellEditingStarted skip: row has no data/identifier');
            return;
        }
        acquireRowLock(internalTancId);
    }, [sessionDetails?.sessionId, sessionDetails?.tancId, rowLockingEnabled]);

    // When a cell edit ends, release the row's lock if it has no unsaved work (so a
    // no-op edit frees it now, not at TTL). Only release path for clicks off-grid.
    const onCellEditingStopped = useCallback((params) => {
        markActivity();
        if (!rowLockingEnabled || savingRef.current) return;
        const data = params?.data;
        const internalTancId = data?.internal_tanc_identifier;
        if (!internalTancId) return;
        if (!heldLocksRef.current.has(internalTancId)) {
            // Released before this edit committed (e.g. focus moved first): claim it again.
            if (rowHasUnsavedWork(data)) acquireRowLock(internalTancId);
            return;
        }
        if (deletedIdentifiersRef.current.has(internalTancId)) {
            console.log(`[RowLock] cell edit stopped row=${internalTancId}: pending delete → keeping lock`);
            return;
        }
        if (!rowHasUnsavedWork(data)) {
            // Release only once the user has LEFT the row; still in it (e.g. Enter) keeps the lock,
            // and onCellFocused releases it when they move to another row.
            // Decide after focus settles: Enter keeps focus in the grid on this row (keep);
            // clicking anywhere outside the grid, or into another row, means they left (release).
            setTimeout(() => {
                const focused = params.api.getFocusedCell?.();
                const focusedRowId = focused ? params.api.getDisplayedRowAtIndex(focused.rowIndex)?.data?.internal_tanc_identifier : null;
                const inGrid = Boolean(gridWrapperRef.current?.contains(document.activeElement));
                if (inGrid && focusedRowId === internalTancId) {
                    console.log(`[RowLock] cell edit stopped row=${internalTancId}: no change, still in row → keeping lock`);
                    return;
                }
                if (rowIdHasUnsavedWork(internalTancId) || !heldLocksRef.current.has(internalTancId)) return;
                console.log(`[RowLock] cell edit stopped row=${internalTancId}: no change, left row → releasing lock`);
                releaseRowLock(internalTancId);
            }, 0);
        } else {
            console.log(`[RowLock] cell edit stopped row=${internalTancId}: has unsaved changes → keeping lock`);
        }
    }, [rowLockingEnabled, sessionDetails?.sessionId, sessionDetails?.tancId]);

    // A single click only selects — the row is locked on edit-start (onCellEditingStarted), not here.
    const onCellClicked = useCallback(() => {
        markActivity();
    }, []);

    // When focus moves to a different row, free the row we left if it's held and unchanged
    // (keying on the row, not cell edit-start/stop, avoids a double-click self-racing its own lock).
    const onCellFocused = useCallback((params) => {
        markActivity();
        setSelectedRowIndex(params.rowIndex)

        if (rowLockingEnabled && !savingRef.current) {
            const focusedData = params?.rowIndex != null
                ? params.api.getDisplayedRowAtIndex(params.rowIndex)?.data
                : null;
            const newRowUid = focusedData?.internal_tanc_identifier ?? null;
            const prevRowUid = prevFocusedRowRef.current?.internal_tanc_identifier ?? null;
            const prevData = (prevRowUid && params.api.getRowNode(prevRowUid)?.data) || prevFocusedRowRef.current;

            // Never release a row whose edit is still in progress (value not committed yet);
            // onCellEditingStopped decides once the value lands.
            const prevRowStillEditing = (params.api.getEditingCells?.() || []).some(c =>
                params.api.getDisplayedRowAtIndex(c.rowIndex)?.data?.internal_tanc_identifier === prevRowUid);
            if (prevRowUid && prevRowUid !== newRowUid
                && heldLocksRef.current.has(prevRowUid)
                && !deletedIdentifiersRef.current.has(prevRowUid)
                && !prevRowStillEditing
                && !rowHasUnsavedWork(prevData)) {
                releaseRowLock(prevRowUid);
            }
            prevFocusedRowRef.current = focusedData;
        }
    }, [rowLockingEnabled, sessionDetails?.sessionId, sessionDetails?.tancId]);

    const onSelectionChanged = (param) => {
        const selectedNodes = gridRef.current.api.getSelectedNodes();
        const selectedData = selectedNodes.map(node => ({
            rowIndex: node.rowIndex,
            data: node.data
        }));
        setMultiSelectedRow(selectedData.sort((a, b) => b.rowIndex - a.rowIndex))
    };

    const onGridReady = useCallback((params) => {
        // gridRef?.current?.api.sizeColumnsToFit();
        const allColumnIds = [];
        gridRef?.current?.api?.getColumns()?.forEach((column) => {
            allColumnIds.push(column.getId());
        });
        gridRef?.current?.api?.autoSizeColumns(allColumnIds);
        updateGridInfo()

    }, []);

    const onRowDataUpdated = useCallback(() => {
        if (gridRef?.current?.api && (formulaStoreRef.current.size > 0 || columnFormulaStoreRef.current.size > 0)) {
            gridRef.current.api.refreshCells({ force: true });
        }
    }, []);

    const isNumber = (value) => {
        try {
            if (typeof value === 'number' && !isNaN(value)) {
                return true;
            }
            // if (typeof value === 'string' && !isNaN(Number(value))) {
            //   return true;
            // }
            return false;
        } catch (e) {
            return false
        }
    };

    const isNumberAfterParsed = (value) => {
        try {
            if (typeof value === 'number' && !isNaN(value)) {
                return true;
            }
            if (typeof value === 'string' && !isNaN(Number(value))) {
                return true;
            }
            return false;
        } catch (e) {
            return false
        }
    };

    const getContextMenuItems = (params) => {
        let menuItems = [
            'copy',
            'copyWithHeaders',
            // 'paste',
            'separator',
            'export'
        ]
        if (props.canEdit) {
            menuItems.push('separator')
            menuItems.push({
                name: 'Clone',
                action: () => {
                    addRowToAnnotation(params.node.data)
                }
            })
        }
        return menuItems;
    }

    function notifyFlashMessages(notification) {
        if (notification) {
            let flashMessages = []
            for (const i in notification.messages) {
                flashMessages.push({
                    header: notification.header,
                    type: notification.type,
                    dismissible: true,
                    dismissLabel: "Dismiss message",
                    onDismiss: () => setFlashMessages([]),
                    content: (
                        <>
                            {notification.messages[i]}
                        </>
                    ),
                    id: `message_${i}`
                })
            }
            setFlashMessages(flashMessages)
        }
    }
    function applyFilterModel(model) {
        gridRef.current.api?.setFilterModel(JSON.parse(JSON.stringify(model)));
        gridRef.current.api?.onFilterChanged();
    }

    const getFilterDiff = () => {
        let diff = {};

        if (!gridRef?.current?.api || !tancFilterModel) {
            return diff;
        }
        
        const tancFilters = tancFilterModel || {};
        const currentFilterModel = gridRef.current.api.getFilterModel();
        const currentFilters = filterNullsInObjectArrays(currentFilterModel);

        for (const filterName in currentFilters) {
            const originalFilter = tancFilters[filterName];
            const currentFilter = currentFilters[filterName];

            if (originalFilter) {
                const filteredFilters = currentFilter.filterModels.filter((currentFilterModel) => {
                    return !originalFilter.filterModels.find((originalFilterModel) => deepEqual(originalFilterModel, currentFilterModel))
                })
                filteredFilters.length > 0 && (diff[filterName] = filteredFilters);
            } else {
                diff[filterName] = currentFilter;
            }
        }
        return diff;
    };

    const saveCurrentFilters = async (filterName) => {
        const filterDiff = getFilterDiff();
        const newPreferenceId = uuidv4();
        const createFilter = new Promise((resolve, reject) => {
            createPreferenceWithExtras(
                newPreferenceId,
                `CUSTOM_FILTER#${userId}`,
                filterName,
                props.tancDetails.tanc_id, 
                'TANC',
                userId,
                filterDiff,
                {originalFilterId: selectedFilterOption.value},
                null, // ttl
                () => {
                    setShowSaveCurrentFiltersModal(false);

                    const filter = {
                        label: filterName,
                        value: newPreferenceId,
                        extra: {originalFilterId: selectedFilterOption.value},
                        tags: [PERSONAL_TAG, CUSTOM_TAG]
                    }

                    const localCustomFilters = structuredClone(customFilters);
                    localCustomFilters[newPreferenceId] = {
                        ...filter,
                        extras: JSON.stringify({originalFilterId: selectedFilterOption.value}),
                        information: JSON.stringify(filterDiff)
                    };
                    setCustomFilters(localCustomFilters);
                    
                    const localFilterThatApplyToAlias = structuredClone(filterThatApplyToAlias);
                    localFilterThatApplyToAlias.push(filter);
                    
                    setFilterThatApplyToAlias(localFilterThatApplyToAlias)
                    resolveRoleMap(localFilterThatApplyToAlias);
                    setSelectedFilterOption(filter);
                },
                (createError) => {
                    reject(createError)
                }
            );
        })
        await createFilter;
        return filterDiff || {};
    };

    const deleteCurrentFilters = (customFilter) => {
        const originalFilterId = customFilter?.extra?.originalFilterId;
        const customFilterId = customFilter?.value;
        deletePreference(customFilterId, null, () => {
            const filter = filterThatApplyToAlias.find(filter => filter.value === originalFilterId);
            setSelectedFilterOption(filter);

            const filteredFilterthatApplyToAlias = filterThatApplyToAlias.filter(filter => filter.value != customFilterId)
            const localFilterThatApplyToAlias = structuredClone(filteredFilterthatApplyToAlias);
            setFilterThatApplyToAlias(localFilterThatApplyToAlias)

            const localCustomFilters = structuredClone(customFilters);
            delete localCustomFilters[customFilterId];
            setCustomFilters(localCustomFilters);
        }, (error) => {
            console.error("ERROR", error)
        })
    }

    const onFilterChanged = (event) => {
        setHasFilterDiff(Object.keys(getFilterDiff()).length > 0);
        if (event.source != 'columnFilter' || event?.columns?.length <= 0) {
            return
        }
        const gridApi = gridRef.current.api;
        const currentModel = gridApi?.getFilterModel();
        let missingColumns = []
        for (const colName in tancFilterModel) {
            if (!currentModel.hasOwnProperty(colName)) {
                currentModel[colName] = tancFilterModel[colName]
                missingColumns.push(colName)
            }
        }
        if (missingColumns.length > 0) {
            applyFilterModel(currentModel)
        }
        const columnField = event?.columns[0]?.getColDef()?.field;
        if (tancFilterModel?.hasOwnProperty(columnField) && JSON.stringify(currentModel[columnField]) !==
            JSON.stringify(tancFilterModel[columnField])) {
            // replace the set filtertype, leave the remaining
            if (!currentModel.hasOwnProperty(columnField)) {
                // the SET filter that is default applied is removed
                currentModel[columnField] = tancFilterModel[columnField]
            } else {
                let foundSet = false
                if (currentModel[columnField]?.filterModels == null) {
                    currentModel[columnField].filterModels = []
                }
                for (const i in currentModel[columnField]?.filterModels) {
                    if (currentModel[columnField]?.filterModels[i]?.filterType == 'set') {
                        // replace this
                        foundSet = true
                        // is changed?
                        if (JSON.stringify(currentModel[columnField].filterModels[i].values || []) !== JSON.stringify(tancFilterModel[columnField].filterModels[0].values || [])) {
                            notifyFlashMessages({ 'type': 'warning', header: 'Default filter keys cannot be changed.', messages: [`Altering the filters for ${Object.keys(tancFilterModel)} is not allowed.`] })
                        }
                        currentModel[columnField].filterModels[i] = tancFilterModel[columnField].filterModels[0]
                    }
                }
                if (!foundSet) {
                    // the SET filter that is default applied is removed/changed
                    currentModel[columnField].filterModels.push(tancFilterModel[columnField].filterModels[0])
                }
            }
            applyFilterModel(currentModel)
        } else {
            updateGridInfo();
        }
    };

    const onViewportChanged = () => {
        updateGridInfo();
    };

    const updateGridInfo = () => {
        if (gridRef?.current?.api) {
            const api = gridRef.current.api;
            setGridInfo({
                rowCount: api?.getDisplayedRowCount(),
                totalRows: dsInfo?.length,
            });
        }
    };

    function resetSaveRequestModal(){
        setShowSaveModal(false)
        setSaveRequestComment(null)
    }
    function resetSyncRequestModal() {
        setSyncLoaderMessage(null)
        setShowSyncRequestCommentModal(false)
        setSyncRequestComment(null)
    }

    function resetHistoryModal() {
        setShowHistoryModal(false)
        setHistoryErrorMessage(null)
        setHistoryLoaderMessage(null)
    }

    function getHistory() {
        if (sessionDetails?.sessionId) {
            setHistoryLoaderMessage("Fetching history..")
            listTANCVersions(sessionDetails?.sessionId, function onSuccess(response) {
                const sortedSet = new Set(
                    [...response?.data?.listTANCVersions].sort((a, b) => b.lastUpdatedTimestamp - a.lastUpdatedTimestamp)
                );
                if (sortedSet) {
                    setHistoryData(Array.from(sortedSet))
                }
                setHistoryLoaderMessage(null)
            }, function onFailure(error) {
                setHistoryData([])
                setHistoryLoaderMessage(null)
                setHistoryErrorMessage(`Error fetching versions. ${error?.errors[0]?.message}`)
            })
        }
    }

    function requestSync() {
        if (syncRequestComment) {
            setSyncLoaderMessage(`Requesting data sync from ${props?.tancDetails?.upstream_type}...`)
            tancSync(sessionDetails.sessionId, "UPDATE", "ABC", syncRequestComment, function onSuccess(response) {
                resetSyncRequestModal()
            }, function onFailure(error) {
                setRequestSyncCommentErrorMessage(`Failed to sync. ${error?.errors[0]?.message}`)
            })
        } else {
            // error
            setSyncLoaderMessage(null)
            setRequestSyncCommentErrorMessage('Comments are required for requesting data sync')
        }
    }

    const onRangeSelectionChanged = useCallback((event) => {
        setAggregationValues(null)
        const gridApi = event.api;
        const cellRanges = gridRef?.current?.api?.getCellRanges();
        let count = 0
        let min = null
        let max = null
        let sum = 0

        if (cellRanges) {
            cellRanges.forEach(range => {
                range.columns.forEach(column => {
                    const colId = column?.getColId();
                    const valueGetter = column?.getColDef()?.valueGetter;

                    for (let rowIndex = range?.startRow?.rowIndex; rowIndex <= range?.endRow?.rowIndex; rowIndex++) {
                        const node = gridApi?.getDisplayedRowAtIndex(rowIndex);
                        if (!node) continue;

                        let value;
                        if (valueGetter) {
                            value = valueGetter({
                                data: node?.data,
                                node: node,
                                column: column,
                                api: gridApi
                            });
                        } else {
                            value = node?.data[colId];
                        }

                        if (!isNumber(value)) {
                            continue
                        }
                        count = count + 1
                        sum = sum + value
                        if (min == null || value < min) {
                            min = value
                        }
                        if (max == null || value > max) {
                            max = value
                        }
                    }
                });
            });
        }
        if (count > 0) {
            setAggregationValues({
                'average': formatFloatNumber(sum / count),
                'count': count,
                'sum': formatFloatNumber(sum),
                'min': formatFloatNumber(min),
                'max': formatFloatNumber(max)
            })
        }

    }, [])

    const onColumnRowGroupChanged = useCallback(() => {
        setRowGroupColumns(gridRef?.current?.api?.getRowGroupColumns())
        updateGridInfo()
    }, [])

    // Enter edit mode locally — no shared session lock; per-row locks handle
    // same-row contention while others edit concurrently.
    const startEditing = () => {
        if (!rowLockingEnabled) {
            // FRISCO: acquire the full-session lock, exactly as before row locking.
            lockSession();
            return;
        }
        if (sessionDetails?.sessionId) {
            createAuditLog(`TANC_SESSION#${sessionDetails.sessionId}`, `START_EDITING#${Date.now()}`, "Entering per-row edit mode", JSON.stringify(sessionDetails), null, "TANC START EDITING", userId)
        }
        setIsEditing(true);
    };

    // Exit edit mode and clear pending state. release=true (cancel) RELEASEs held
    // rows; release=false (submit) only clears heldLocksRef so the orchestrator wins.
    const exitEditing = ({ release = true } = {}) => {
        if (rowLockingEnabled) {
            if (release) {
                releaseHeldLocks();
                props?.onDiscardRowLockDraft?.();
            } else {
                heldLocksRef.current.clear();
            }
            setIsEditing(false);
        } else {
            // FRISCO: release the full-session lock, exactly as before.
            unlockSession();
        }
        setHasUnsavedFormula(false);
        setPendingFormula(null);
        setPendingFormulaRemoval(null);
        setShowColumnFormulaOverrideWarning(null);
        temporaryEditFormulaCellsRef.current.clear();
    };

    const lockSession = () => {
        if (sessionDetails?.sessionId) {
            createAuditLog(`TANC_SESSION#${sessionDetails.sessionId}`, `LOCK_SESSION#${Date.now()}`, "Acquiring lock for the session from Accounting central", JSON.stringify(sessionDetails), null, "TANK LOCK SESSION ON UI", userId)
            updateTANCLock(sessionDetails.sessionId, true, userId, "Editing data", function onSuccess(response) {
                setSessionDetails({ ...sessionDetails, isLocked: response?.data?.updateTANCLock?.isLocked, lockedBy: response?.data?.updateTANCLock?.lockedBy })
            }, function onFailure(response) {
                setAnnotationError("Failed to lock!")
            })
        }
    }

    const unlockSession = () => {
        if (sessionDetails?.sessionId) {
            // Phase 2: releasing the session lock ends this user's edit
            // window, so release any per-row locks we're still holding.
            releaseHeldLocks();
            createAuditLog(`TANC_SESSION#${sessionDetails.sessionId}`, `UNLOCK_SESSION#${Date.now()}`, "Releasing lock for the session from Accounting central", JSON.stringify(sessionDetails), null, "TANK UNLOCK SESSION ON UI", userId)
            updateTANCLock(sessionDetails.sessionId, false, null, null, function onSuccess(response) {
                setSessionDetails({ ...sessionDetails, isLocked: response?.data?.updateTANCLock?.isLocked, lockedBy: response?.data?.updateTANCLock?.lockedBy })
                setHasUnsavedFormula(false);
                setPendingFormula(null);
                setPendingFormulaRemoval(null);
                setShowColumnFormulaOverrideWarning(null);
                temporaryEditFormulaCellsRef.current.clear();
            }, function onFailure(response) {
                setAnnotationError("Failed to unlock!")
            })
        }
    }

    function getCleanedS3Key(uri) {
        if (!uri) throw new Error('S3 URL is required');
        let key=null
        let prefix=null
        if(uri.startsWith('s3://')){
            uri = uri.replace('s3://', '');
            let [bucketName, uriPrefix, ...keyParts] = uri.split('/');
            if (!bucketName) throw new Error('Invalid S3 URL: no bucket name');
            prefix = uriPrefix
            key = keyParts.join('/');
        }else{
            let [uriPrefix, ...keyParts] = uri.split('/');
            prefix = uriPrefix
            key = keyParts.join('/');
        }
        return [prefix, key]
    }

    function resubmitVersionPrompt(versionData) {
        setShowHistoryModal(null)
        if (!(undoEditCount <= 0 || rowGroupColumns?.length > 0)) {
            setResubmitVersionData(versionData)
        } else {
            resubmitVersion(versionData)
        }

    }
    function resetResubmitModal() {
        setResubmitVersionData(null)
    }

    async function resubmitVersion(versionData, resetView = false) {
        resetResubmitModal()
        cleanViewBeforeLoad(resetView)
        configureTANCS3()
        getPresignedUrlForVersionFile(sessionDetails?.sessionId, versionData.version, function onSuccess(response) {
            let presigned_url = JSON.parse(response?.data?.getPresignedUrlForVersionFile)?.url
            d3.csv(presigned_url).then((result) => {
                setResubmitTimestamp({ 'timestamp': versionData?.lastUpdatedTimestamp, 'versionData': versionData })
                setViewOnTheGrid(result)
                resetHistoryModal()
            }).catch((response) => {
                setAnnotationLoader(null)
                setAnnotationError('Loading failed')
            });
        }, function onFailure(response) {
            setAnnotationError("Failed to fetch the version file!")
        })
    }

    async function downloadVersionFile(uri, version, bucket = null) {
        if (sessionDetails?.sessionId) {
            getPresignedUrlForVersionFile(sessionDetails?.sessionId, version, function onSuccess(response) {
                let presigned_url = JSON.parse(response?.data?.getPresignedUrlForVersionFile)?.url
                window.open(presigned_url)
            }, function onFailure(response) {
                setHistoryErrorMessage("Failed to fetch the version file!")
            })
        }
    }


    async function downloadFile(uri, bucket = null) {
        
        let [prefix, key] = getCleanedS3Key(uri)
        let arr = key.split('.')
        let contentType = arr[arr.length - 1]
        let service;
        if (bucket == null || bucket?.toLowerCase()?.endsWith('tanc')) {
            configureTANCS3()
            service = VAULT_SERVICES.TANC
        } else if (bucket?.toLowerCase()?.endsWith('frisco')) {
            configureFriscoS3()
            service = VAULT_SERVICES.FRISCO
        } else if (bucket?.toLowerCase()?.endsWith('flexico')) {
            configureFlexicoS3()
            service = VAULT_SERVICES.FLEXICO
        }
        // let result = await Storage.get(key, { level: 'public', customPrefix: { public: `${prefix}/` }, expires: 60 });
        let serviceParams = {"file_path": `${prefix}/${key}`}
        // Pass the TANC identifiers for all services; path validation still
        // constrains access to the correct S3 prefix for FRISCO/FLEXICO.
        if (sessionDetails?.tancId) serviceParams.service_id = `${sessionDetails.tancId}`
        if (sessionDetails?.sessionId) serviceParams.app_id = `${sessionDetails.sessionId}`
        let document = await getDocumentFromVault(contentType, service, serviceParams)                
        let fileURL = document?.fileUrl
        window.open(fileURL);
    }

    // using this variable to check user access
    function resolveRoleMap(filtersThatApply) {
        let userMapRole = {isAllowedUser: false, isAdmin: false, isEditor: false, isReviewer: false}
        for(let filter of filtersThatApply){
            userMapRole['isAllowedUser'] = true
            if (filter?.extra?.role == 'ADMIN') {
                userMapRole['isAdmin'] = true
            }
            if (filter?.extra?.role == 'EDITOR') {
                userMapRole['isEditor'] = true
            }
            if (filter?.extra?.role == 'REVIEWER') {
                userMapRole['isReviewer'] = true
            }
        }
        setTancUserRoleMap(userMapRole)
    }

    function findAndApplyFilters(ldaps, permissions, filters, customFilters) {
        let filtersThatApply = []
        let ldapDefaultFilter = null
        for (const i in permissions) {
            let permission = permissions[i]
            if (permission?.type == 'USER' && permission.alias == userId) {
                let option = {
                    'label': filters[permission.filter]?.label, 'value': permission.filter,
                    'extra': permission, tags: [permission.role]
                }
                ldapDefaultFilter = option
                setSelectedFilterOption(option)
                filtersThatApply.push(option)
            } else if (permission?.type == 'LDAP' && ldaps?.includes(permission.alias)) {
                let filterVal = {
                    'label': filters[permission.filter]?.label,
                    'value': permission.filter, 'extra': permission, tags: [permission.role, permission.alias]
                }
                filtersThatApply.push(filterVal)
                if (ldapDefaultFilter == null || filters[permission.filter]?.lastUpdatedTimestamp < ldapDefaultFilter?.lastUpdatedTimestamp) {
                    filterVal['lastUpdatedTimestamp'] = filters[permission.filter]?.lastUpdatedTimestamp || 9999999999
                    ldapDefaultFilter = filterVal
                }
            }
        }

        Object.keys(customFilters).forEach(customFilterKey => {
            const customFilter = customFilters[customFilterKey];
            const option = {
                label: customFilter.label,
                value: customFilter.preferenceId,
                extra: JSON.parse(customFilter.extras),
                tags: [PERSONAL_TAG, CUSTOM_TAG]
            }
            filtersThatApply.push(option);
        })

        if (selectedFilterOption == null && ldapDefaultFilter) {
            setSelectedFilterOption(ldapDefaultFilter)
        }

        resolveRoleMap(filtersThatApply)
        setFilterThatApplyToAlias(filtersThatApply)
    }

    function loadAllPreferences(onResult) {
        if (props?.tancDetails?.tanc_id) {
            listPreference(props.tancDetails.tanc_id, 'TANC', null, function onSuccess(response) {
                let items = response?.data?.listPreference?.items || []
                let filters = {}
                for (const i in items) {
                    if (items[i].preferenceType == 'FILTER') {
                        filters[items[i].preferenceId] = items[i]
                    }
                }
                let customFilters = {}
                for (const i in items) {
                    if (items[i].preferenceType == `CUSTOM_FILTER#${userId}`) {
                        customFilters[items[i].preferenceId] = items[i]
                    }
                }
                setFilters(filters)
                setCustomFilters(customFilters)
                for (const i in items) {
                    if (items[i].preferenceType == 'CONFIGURATION') {
                        let info = JSON.parse(items[i].information)
                        let transformedConfigurationMapper = {}
                        for (const i in info) {
                            transformedConfigurationMapper[info[i].column_name] = info[i]
                        }
                        setConfigurationMap(transformedConfigurationMapper)
                    } else if (items[i].preferenceType == 'PERMISSIONS') {
                        let information = JSON.parse(items[i].information)
                        // check if the user id belongs to one of aliases or users
                        //  fetch all ldaps for the alias and compare
                        getBrassAllLDAPS(currentStage, function onSuccess(response) {
                            let ldaps = response?.data?.getBrassAllLDAPS
                            findAndApplyFilters(ldaps, information, filters, customFilters)
                        }, function onFailure(response) {
                            findAndApplyFilters(null, information, filters, customFilters)
                        })
                    } else if (items[i].preferenceType == 'TANC-FORMULAS') {
                        try {
                            const formulaData = JSON.parse(items[i].information)
                            const formulas = formulaData?.formulas || {}
                            const columnFormulas = formulaData?.columnFormulas || {}
                            setSavedFormulas(formulas)
                            setSavedColumnFormulas(columnFormulas)
                            setFormulaPreferenceId(items[i].preferenceId)
                            // Populate the cell-level formula store
                            Object.entries(formulas).forEach(([key, formula]) => {
                                formulaStoreRef.current.set(key, formula);
                            });
                            // Store column formulas for persistence tracking
                            Object.entries(columnFormulas).forEach(([key, value]) => {
                                columnFormulaStoreRef.current.set(key, value);
                            });
                        } catch (e) {
                            setSavedFormulas({})
                            setSavedColumnFormulas({})
                        }
                    }
                }
                if (onResult) {
                    onResult(response)
                }
            }, function onFailure(response) {
            })
        }
    }

    const getDate = (value) => {
        try {
            var d = new Date(Number(value))
            return d.toLocaleString()
        } catch (e) {
            return ''
        }
    }

    const isCustomFilterSelected = () => selectedFilterOption?.tags[0] === PERSONAL_TAG;

    const formulaStoreRef = useRef(new Map());
    const columnFormulaStoreRef = useRef(new Map());
    const temporaryEditFormulaCellsRef = useRef(new Set());

    const totalRowsRef = useRef(0);

    const buildFormulaForRow = (formula, anchorRowIndex, targetRowIndex) => {
        const totalRows = totalRowsRef.current;
        const offset = targetRowIndex - anchorRowIndex;

        if (offset === 0) return formula;

        // Apply offset to each ROW reference
        let result = formula.replace(/ROW\("(\d+)"\)/g, (match, row) => {
            const newRow = parseInt(row, 10) + offset;
            return `ROW("${newRow}")`;
        });

        // Replace any REF containing an out-of-bounds ROW with 0
        result = result.replace(/REF\((?:[^()]*|\([^()]*\))*\)/g, (refMatch) => {
            let hasOutOfBounds = false;
            refMatch.replace(/ROW\("(-?\d+)"\)/g, (m, r) => {
                const rowNum = parseInt(r, 10);
                if (rowNum < 0 || rowNum >= totalRows) {
                    hasOutOfBounds = true;
                }
                return m;
            });
            return hasOutOfBounds ? '0' : refMatch;
        });

        return result;
    };

    const clearCellFormulasForColumn = (colId) => {
        formulaStoreRef.current.forEach((_, key) => {
            if (key.endsWith(`-${colId}`)) {
                formulaStoreRef.current.delete(key);
            }
        });
    };

    const restoreFormulasFromSaved = () => {
        formulaStoreRef.current.clear();
        columnFormulaStoreRef.current.clear();
        if (savedFormulas) {
            Object.entries(savedFormulas).forEach(([key, formula]) => {
                formulaStoreRef.current.set(key, formula);
            });
        }
        if (savedColumnFormulas) {
            Object.entries(savedColumnFormulas).forEach(([key, value]) => {
                columnFormulaStoreRef.current.set(key, value);
            });
        }
    };

    const resetFormulaUIState = () => {
        temporaryEditFormulaCellsRef.current.clear();
        setPendingFormula(null);
        setPendingFormulaRemoval(null);
        setShowColumnFormulaOverrideWarning(null);
    };

    const applyColumnFormulaToRefs = (colId, formula, anchorRowIndex) => {
        columnFormulaStoreRef.current.set(colId, { formula, anchorRowIndex });
        clearCellFormulasForColumn(colId);
        temporaryEditFormulaCellsRef.current.clear();
    };

    const applyFormulaToSingleCell = useCallback((rowId, colId, formula) => {
        const key = `${rowId}-${colId}`;
        if (formula === undefined) {
            formulaStoreRef.current.delete(key);
        } else {
            formulaStoreRef.current.set(key, formula);
        }
        setHasUnsavedFormula(true);
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
        }
    }, []);

    const applyFormulaToColumn = () => {
        if (!pendingFormula) return;
        if (columnFormulaStoreRef.current.has(pendingFormula.colId)) {
            const { colId, formula, anchorRowIndex } = pendingFormula;
            applyColumnFormulaToRefs(colId, formula, anchorRowIndex);
            if (gridRef?.current?.api) {
                gridRef.current.api.refreshCells({ columns: [colId], force: true });
            }
            setShowColumnFormulaOverrideWarning('column');
            return;
        }
        confirmApplyFormulaToColumn();
    };

    const confirmApplyFormulaToColumn = () => {
        if (!pendingFormula) return;
        const { colId, formula, anchorRowIndex } = pendingFormula;
        applyColumnFormulaToRefs(colId, formula, anchorRowIndex);
        setPendingFormula(null);
        setShowColumnFormulaOverrideWarning(null);
        setHasUnsavedFormula(true);
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({ columns: [colId], force: true });
        }
        saveFormulas();
    };

    const confirmRemoveFormula = () => {
        if (!pendingFormulaRemoval) return;
        const { colId, rowId, type } = pendingFormulaRemoval;
        if (type === 'column') {
            columnFormulaStoreRef.current.delete(colId);
            clearCellFormulasForColumn(colId);
        } else {
            formulaStoreRef.current.delete(`${rowId}-${colId}`);
        }
        temporaryEditFormulaCellsRef.current.clear();
        setPendingFormulaRemoval(null);
        setHasUnsavedFormula(true);
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({ columns: [colId], force: true });
        }
        saveFormulas();
    };

    const cancelFormulaRemoval = () => {
        temporaryEditFormulaCellsRef.current.clear();
        setPendingFormulaRemoval(null);
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
        }
    };

    const cancelFormulaOverride = () => {
        resetFormulaUIState();
        restoreFormulasFromSaved();
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
        }
    };

    const formulaDataSource = useMemo(() => ({
        getFormula: ({ column, rowNode }) => {
            const colId = column.getColId();
            const key = `${rowNode.id}-${colId}`;
            // Cell-level formula takes priority for immediate preview
            const cellFormula = formulaStoreRef.current.get(key);
            if (cellFormula) {
                return cellFormula;
            }
            // Skip column formula for cells where user typed a plain value (pending removal)
            if (temporaryEditFormulaCellsRef.current.has(key)) {
                return undefined;
            }
            // Fall back to column-level formula
            const colFormula = columnFormulaStoreRef.current.get(colId);
            if (colFormula) {
                const rowRef = parseInt(rowNode.id, 10);
                if (!isNaN(rowRef)) {
                    return buildFormulaForRow(colFormula.formula, colFormula.anchorRowIndex, rowRef);
                }
            }
            return undefined;
        },
        setFormula: ({ column, rowNode, formula }) => {
            const colId = column.getColId();
            if (formula === undefined) {
                // Formula is being removed
                if (columnFormulaStoreRef.current.has(colId)) {
                    temporaryEditFormulaCellsRef.current.add(`${rowNode.id}-${colId}`);
                    setPendingFormulaRemoval({ colId, rowId: rowNode.id, type: 'column' });
                } else if (formulaStoreRef.current.has(`${rowNode.id}-${colId}`)) {
                    temporaryEditFormulaCellsRef.current.add(`${rowNode.id}-${colId}`);
                    setPendingFormulaRemoval({ colId, rowId: rowNode.id, type: 'cell' });
                } else {
                    applyFormulaToSingleCell(rowNode.id, colId, undefined);
                }
            } else {
                applyFormulaToSingleCell(rowNode.id, colId, formula);
                if (column.getColDef().allowFormula) {
                    setPendingFormula({
                        colId: colId,
                        formula: formula,
                        anchorRowIndex: parseInt(rowNode.id, 10),
                        rowId: rowNode.id
                    });
                }
            }
        },
    }), []);

    const saveSingleCellFormula = () => {
        // Check if the pending formula's column has a column formula
        if (pendingFormula && columnFormulaStoreRef.current.has(pendingFormula.colId)) {
            setShowColumnFormulaOverrideWarning('single');
            return;
        }
        setPendingFormula(null);
        setHasUnsavedFormula(true);
        saveFormulas();
    };

    const confirmSaveSingleCellFormula = () => {
        if (pendingFormula) {
            const { colId, formula, rowId } = pendingFormula;
            columnFormulaStoreRef.current.delete(colId);
            clearCellFormulasForColumn(colId);
            temporaryEditFormulaCellsRef.current.clear();
            formulaStoreRef.current.set(`${rowId}-${colId}`, formula);
        }
        setShowColumnFormulaOverrideWarning(null);
        setPendingFormula(null);
        setHasUnsavedFormula(true);
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
        }
        saveFormulas();
    };

    const saveFormulas = () => {
        if (!props?.tancDetails?.tanc_id) return;
        const isAdminRole = selectedFilterOption?.extra?.role == 'ADMIN';
        if (!isAdminRole) return;

        const formulas = {};
        formulaStoreRef.current.forEach((formula, key) => {
            formulas[key] = formula;
        });

        const columnFormulas = {};
        columnFormulaStoreRef.current.forEach((value, key) => {
            columnFormulas[key] = value;
        });

        const formulaData = { formulas, columnFormulas, lastUpdatedBy: userId, lastUpdatedAt: Date.now() };

        const getFormulaExpression = (val) => typeof val === 'object' && val !== null ? (val.formula || JSON.stringify(val)) : val;

        const buildFormulaAuditComment = () => {
            const prevFormulas = { ...(savedFormulas || {}), ...(savedColumnFormulas || {}) };
            const currFormulas = { ...formulas, ...columnFormulas };
            const added = Object.keys(currFormulas).filter(k => !prevFormulas[k]);
            const removed = Object.keys(prevFormulas).filter(k => !currFormulas[k]);
            const parts = [`TANC ${props.tancDetails.tanc_id}, session ${sessionDetails?.sessionId}.`];
            if (added.length) parts.push(`Formula(s) added: ${added.map(k => getFormulaExpression(currFormulas[k])).join(', ')}.`);
            if (removed.length) parts.push(`Formula(s) removed: ${removed.map(k => getFormulaExpression(prevFormulas[k])).join(', ')}.`);
            if (!added.length && !removed.length) parts.push(`Formula(s) updated: ${Object.values(currFormulas).map(v => getFormulaExpression(v)).join(', ')}.`);
            return JSON.stringify(parts.join(' ')).slice(1, -1);
        };

        if (formulaPreferenceId) {
            updatePreferenceWithExtras(
                formulaPreferenceId,
                'Session Formulas',
                userId,
                formulaData,
                { sessionId: sessionDetails?.sessionId, version: (Object.keys(savedFormulas || {}).length > 0 ? 2 : 1) },
                function onSuccess() {
                    createAuditLog(`FRISCO#${props.tancDetails.association_id}`, `${Date.now()}`, buildFormulaAuditComment(), JSON.stringify({ formulas: savedFormulas, columnFormulas: savedColumnFormulas }), JSON.stringify(formulaData), "SAVE_FORMULAS", userId);
                    setSavedFormulas(formulas);
                    setSavedColumnFormulas(columnFormulas);
                    setAnnotationSaveSuccessMessage("Formula saved successfully");
                    setTimeout(() => setAnnotationSaveSuccessMessage(null), 3000);
                },
                function onFailure(error) {
                    console.error('Failed to save formulas:', error);
                    setAnnotationError("Failed to save formula");
                }
            );
        } else {
            const newId = uuidv4();
            createPreferenceWithExtras(
                newId,
                'TANC-FORMULAS',
                'Session Formulas',
                props.tancDetails.tanc_id,
                'TANC',
                userId,
                formulaData,
                { sessionId: sessionDetails?.sessionId, version: 1 },
                null,
                function onSuccess() {
                    createAuditLog(`FRISCO#${props.tancDetails.association_id}`, `${Date.now()}`, buildFormulaAuditComment(), JSON.stringify({}), JSON.stringify(formulaData), "SAVE_FORMULAS", userId);
                    setFormulaPreferenceId(newId);
                    setSavedFormulas(formulas);
                    setSavedColumnFormulas(columnFormulas);
                    setAnnotationSaveSuccessMessage("Formula saved successfully");
                    setTimeout(() => setAnnotationSaveSuccessMessage(null), 3000);
                },
                function onFailure(error) {
                    console.error('Failed to create formula preference:', error);
                    setAnnotationError("Failed to save formula");
                }
            );
        }
    };

    return (
        <SpaceBetween direction="vertical" size="m">
            {
                flashMessages && <Flashbar items={flashMessages} />
            }
            {
                resubmitTimestamp && <Grid
                    gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}
                >
                    <Alert type="warning">
                        This data was previously loaded on {getDate(resubmitTimestamp?.['timestamp'])}
                    </Alert>
                    {
                        props?.showSubmit && sessionDetails?.status != 'INACTIVE'  && <Button variant='primary' onClick={(() => submitAnnotated())} disabled={rowLockingEnabled ? false : sessionDetails?.isLocked}>Submit</Button>
                    }
                    {
                        props?.showSave && sessionDetails?.status != 'INACTIVE'  && <Button variant='primary' onClick={(() => setShowSaveModal(true))} disabled={sessionDetails?.isLocked && sessionDetails?.lockedBy != userId}>Save</Button>
                    }
                    
                </Grid>
            }
            <SpaceBetween direction="vertical">
                <Grid
                    gridDefinition={[
                        { colspan: { default: 3, xxs: 3 } },
                        { colspan: { default: 9, xxs: 9 } }
                    ]}
                >
                    <Box padding="m">
                        <SpaceBetween direction="horizontal" size="xs">
                            {annotationLoader ? <Box> <Spinner></Spinner>{annotationLoader}</Box> : <></>}
                            {annotationError ? <Box> <span className="error_message">{annotationError}</span> </Box> : <></>}
                            {
                                (selectedFilterOption?.extra?.role == 'ADMIN' && (!(undoEditCount <= 0 || rowGroupColumns?.length > 0) || hasUnsavedFormula)) ? <Box><StatusIndicator type="warning">Attention: You have unsaved work</StatusIndicator></Box> : ''
                            }
                            {
                                pendingFormula && !showColumnFormulaOverrideWarning && inEditMode && selectedFilterOption?.extra?.role == 'ADMIN' ? <Box><SpaceBetween direction="vertical" size="s">{columnFormulaStoreRef.current.has(pendingFormula.colId) && <StatusIndicator type="warning">Saving the current formula change will remove the existing formula applied to this column/cell.</StatusIndicator>}<StatusIndicator type="info">A formula has been submitted. Would you like to save it?</StatusIndicator><SpaceBetween direction="horizontal" size="xs"><Button variant="primary" onClick={() => saveSingleCellFormula()}>Save formula for cell</Button><Button variant="primary" onClick={() => applyFormulaToColumn()}>Save formula for column</Button></SpaceBetween></SpaceBetween></Box> : ''
                            }
                            {
                                pendingFormulaRemoval && inEditMode && selectedFilterOption?.extra?.role == 'ADMIN' ? <Box><SpaceBetween direction="horizontal" size="xs"><StatusIndicator type="warning">{pendingFormulaRemoval.type === 'column' ? `The existing column formula for all rows in column: "${pendingFormulaRemoval.colId}" will be removed.` : `The existing formula for this cell will be removed.`}</StatusIndicator><Button variant="primary" onClick={() => confirmRemoveFormula()}>Confirm removal</Button><Button variant="link" onClick={() => cancelFormulaRemoval()}>Cancel</Button></SpaceBetween></Box> : ''
                            }
                            {
                                showColumnFormulaOverrideWarning ? <Box><SpaceBetween direction="horizontal" size="xs"><StatusIndicator type="warning">The previous formula for this column will be removed.</StatusIndicator><Button variant="normal" onClick={() => showColumnFormulaOverrideWarning === 'single' ? confirmSaveSingleCellFormula() : confirmApplyFormulaToColumn()}>Confirm formula update</Button><Button variant="link" onClick={() => cancelFormulaOverride()}>Cancel</Button></SpaceBetween></Box> : ''
                            }
                            {
                                annotationSaveSuccessMessage ? <StatusIndicator type="success">{annotationSaveSuccessMessage}</StatusIndicator> : ''
                            }
                        </SpaceBetween>
                    </Box>
                    <Box float="right" padding="m">
                        <SpaceBetween direction="horizontal" size="xl">
                            {
                                viewers && Object.keys(viewers).length > 0 ?
                                    <Box>
                                        <Popover
                                            header="Viewing the session"
                                            content={<>
                                                {
                                                    Object.keys(viewers).map((key) => {
                                                        return <Box><SpaceBetween direction="horizontal" size="s">
                                                            <img
                                                                alt=""
                                                                className="rounded-circle"
                                                                src={
                                                                    "https://badgephotos.corp.amazon.com/?uid=" +
                                                                    key
                                                                }
                                                                width={10}
                                                                height={10}
                                                            />
                                                            <Box textAlign="center">
                                                                <a
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    href={"https://phonetool.amazon.com/users/" + key}
                                                                >
                                                                    {key}
                                                                </a>
                                                            </Box>
                                                        </SpaceBetween></Box>
                                                    })
                                                }
                                            </>}
                                        >
                                            {Object.keys(viewers).length} viewing
                                        </Popover>
                                    </Box> : <></>
                            }
                            <Button variant="icon" iconName="refresh" onClick={(() => {
                                setResubmitTimestamp(null);
                                formulaStoreRef.current.clear();
                                columnFormulaStoreRef.current.clear();
                                setHasUnsavedFormula(false);
                                setPendingFormula(null);
                                setPendingFormulaRemoval(null);
                                setShowColumnFormulaOverrideWarning(null);
                                getAnnotationInput()
                                loadAllPreferences()
                            })}></Button>
                            <Select
                                selectedOption={selectedFilterOption}
                                onChange={({ detail }) => 
                                    setSelectedFilterOption(detail.selectedOption)
                                }
                                options={filterThatApplyToAlias}
                                triggerVariant="option"
                            />

                            {
                                // Show Save when the parent allows it (showSave) OR whenever a
                                // rebase-save (FLEXICO) user is actually in edit mode — the
                                // row-locking CR let FLEXICO edit-mode start independently of
                                // showSave, which left a save-less edit state. Keep it VISIBLE but
                                // DISABLED until there is an actually-saveable change. Gated on
                                // rebaseSaveEnabled (NOT rowLockingEnabled) so FRISCO — which
                                // persists via Submit re-ingest, not an S3-rebase Save — never
                                // gets this button.
                                ((props?.showSave || (rebaseSaveEnabled && inEditMode)) && sessionDetails?.status != 'INACTIVE') ?
                                    <Button onClick={(() => setShowSaveModal(true))} disabled={(undoEditCount <= 0 && !hasUnsavedFormula) || rowGroupColumns?.length > 0}>Save</Button>
                                    :
                                    <></>
                            }
                            {
                                props?.showSubmit && sessionDetails?.status != 'INACTIVE'?
                                    <Button variant='primary' onClick={(() => submitAnnotated())} disabled={((undoEditCount <= 0 && !hasUnsavedFormula) || rowGroupColumns?.length > 0) || (!inEditMode)}>Submit</Button>
                                    :
                                    <></>
                            }


                            <ButtonDropdown
                                items={[
                                    {
                                        text: "Export to excel",
                                        items: [
                                            { text: "Current view", id: "current_view", disabled: false },
                                            { text: "Selected rows", id: "selected_rows", disabled: false },
                                            { text: "Original", id: "original", disabled: selectedFilterOption?.value != 'All' }
                                        ]
                                    },
                                    {
                                        text: "Import",
                                        items: [
                                            // { text: "Upload from excel", id: "import_csv", disabled: false },
                                            { text: "Add additional data from excel", id: "import_csv_append", disabled: !props.canEdit }
                                        ]
                                    },
                                    {
                                        text: "Manage Custom filters",
                                        items: [
                                            { text: "Save current custom filters", id: "save_filters", disabled: !hasFilterDiff || isCustomFilterSelected() },
                                            { text: "Delete current custom filters", id: "delete_filters", disabled: !isCustomFilterSelected() },
                                        ]
                                    },
                                    { text: "Request sync", id: "request_sync", disabled: !(isPortalAdmin || isSupportAdmin || tancUserRoleMap?.isAdmin) || (props.tancDetails?.association_id_type=='FLEXICO' || sessionDetails?.status == 'INACTIVE') },
                                    { text: "History", id: "history", disabled: !(isPortalAdmin || isSupportAdmin || tancUserRoleMap?.isAllowedUser) }
                                ]}
                                onItemClick={(event) => {
                                    if (event.detail.id == 'current_view') {
                                        exportAnnotated({ 'filtered': true })
                                    } else if (event.detail.id == 'selected_rows') {
                                        exportAnnotated({ 'selectedOnly': true })
                                    }
                                    else if (event.detail.id == 'original') {
                                        exportAnnotated()
                                    } else if (event.detail.id == 'import_csv') {
                                        setShowAnnotationImportModal(true)
                                    } else if (event.detail.id == 'import_csv_append') {
                                        setShowAnnotationImportAppendModal(true)
                                    } else if (event.detail.id == 'request_sync') {
                                        setShowSyncRequestCommentModal(true)
                                    } else if (event.detail.id == 'history') {
                                        getHistory()
                                        setShowHistoryModal(true)
                                    } else if (event.detail.id == 'save_filters') {
                                        setShowSaveCurrentFiltersModal(true);
                                        setCurrentFilterName(`${selectedFilterOption.label ? selectedFilterOption.label : "All"} - `)
                                    } else if (event.detail.id == 'delete_filters') {
                                        deleteCurrentFilters(selectedFilterOption)
                                    }
                                }}
                            >
                                Actions
                            </ButtonDropdown>
                            {/* <Button variant="icon" iconName="unlocked" onClick={(()=>handleViewChanges())}></Button> */}
                            <SpaceBetween direction='vertical' size='m'>
                                {
                                    rowLockingEnabled
                                        ? (!isEditing && selectedFilterOption && isEditorRole() && userId ? <Button disabled={sessionDetails?.status == 'INACTIVE'} onClick={startEditing} variant='primary'>Edit</Button> : <></>)
                                        : (!sessionDetails?.isLocked && selectedFilterOption && isEditorRole() && userId ? <Button disabled={sessionDetails?.isLocked == true || sessionDetails?.status == 'INACTIVE'} onClick={startEditing} variant='primary'>Edit</Button> : <></>)
                                }
                                <SpaceBetween direction='horizontal' size='m'>
                                {
                                    inEditMode && <>
                                            <Button onClick={(() => addRowToAnnotation())} iconName="insert-row">Add</Button>
                                            <Button onClick={(() => delRowToAnnotation())} disabled={multiSelectedRow?.length <= 0} iconName="delete-marker">Delete</Button>
                                        </>
                                }
                                {
                                    inEditMode && <>
                                    <Button onClick={() => {
                                                // Release held row locks first and unconditionally, so a
                                                // cancel always frees the rows for peers even if a later call throws.
                                                releaseHeldLocks();
                                                restoreFormulasFromSaved();
                                                exitEditing()
                                                getAnnotationInput()
                                                loadAllPreferences()
                                            }}>Cancel edit</Button></>
                                }
                                 </SpaceBetween>

                                <SpaceBetween direction='vertical' size='xxxs'>
                                    {
                                        !rowLockingEnabled && sessionDetails?.isLocked && sessionDetails?.lockedBy && sessionDetails?.lockedBy != 'null' ? <Box> <span className='grey_text'>Locked by {sessionDetails?.lockedBy} {sessionDetails.lockLastUpdatedTimestamp ? `at ${new Date(parseInt(sessionDetails.lockLastUpdatedTimestamp)).toLocaleString()}` : ''}</span></Box> : <></>
                                    }
                                    {
                                        !rowLockingEnabled && sessionDetails?.isLocked && sessionDetails?.lockMessage && sessionDetails?.lockedBy != userId ?
                                            <Box><span className='grey_text'>{sessionDetails?.lockMessage}</span></Box> : <></>
                                    }
                                </SpaceBetween>

                            </SpaceBetween>
                        </SpaceBetween>
                    </Box>
                </Grid>
                {
                    selectedFilterOption ?
                        <div style={{
                            width: '100%',
                            height: 'calc(100vh - 200px)',
                        }}>
                            <div
                                ref={gridWrapperRef}
                                className="ag-theme-alpine"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    minHeight: '500px'
                                }}
                            >
                                <AgGridReact
                                    ref={gridRef}
                                    theme="legacy"
                                    rowData={dsInfo}
                                    defaultColDef={defaultProjectColDef}
                                    columnDefs={columnsList}
                                    getRowId={(params) => params.data.internal_tanc_identifier ?? params.data._tancRowId}
                                    getRowStyle={(params) => {
                                        // Grey + not-allowed while locked (peer-held or my own
                                        // in-flight row) until its TANCRowLock record is deleted.
                                        if (isRowLocked(params?.data?.internal_tanc_identifier)) {
                                            return { background: '#eaeded', cursor: 'not-allowed' };
                                        }
                                        return undefined;
                                    }}
                                    formulaDataSource={formulaDataSource}
                                    rowSelection='multiple'
                                    onSelectionChanged={onSelectionChanged}
                                    suppressRowClickSelection={true}
                                    rowMultiSelectWithClick={true}
                                    rowNumbers={true}
                                    // editType={"fullRow"}
                                    animateRows={true}
                                    rowHeight={30}
                                    enableCellTextSelection={false}
                                    undoRedoCellEditing={true}
                                    undoRedoCellEditingLimit={10}
                                    stopEditingWhenCellsLoseFocus={true}
                                    onCellValueChanged={onCellValueChanged}
                                    onCellClicked={onCellClicked}
                                    onCellEditingStarted={onCellEditingStarted}
                                    onCellEditingStopped={onCellEditingStopped}
                                    onCellFocused={onCellFocused}
                                    onGridReady={onGridReady}
                                    onRowDataUpdated={onRowDataUpdated}
                                    onColumnRowGroupChanged={onColumnRowGroupChanged}
                                    rowGroupPanelShow={inEditMode || (formulaStoreRef.current.size > 0) || (columnFormulaStoreRef.current.size > 0) ? "never" : "always"}
                                    sideBar={sideBar}
                                    // groupTotalRow="bottom"
                                    groupAggFiltering={false}
                                    suppressAggFuncInHeader={false}
                                    cellSelection={true}
                                    onRangeSelectionChanged={onRangeSelectionChanged}
                                    getContextMenuItems={getContextMenuItems}
                                    enableContextMenu={true}
                                    // onModelUpdated={onModelUpdated}
                                    onFilterChanged={onFilterChanged}
                                    onViewportChanged={onViewportChanged}
                                    suppressMenuHide={true}
                                    suppressScrollOnNewData={true}
                                    ensureDomOrder={true}
                                // enableAdvancedFilter={true}
                                />
                                {
                                    aggregationValues && aggregationValues?.count > 1 ?
                                        <Box float="right">
                                            <SpaceBetween direction="horizontal" size="s">
                                                <Box><span className="agg-text bold-text">Average:</span> <span className="agg-text">{aggregationValues.average}</span></Box>
                                                <Box><span className="agg-text bold-text">Count:</span> <span className="agg-text">{aggregationValues.count}</span></Box>
                                                <Box><span className="agg-text bold-text">Min:</span> <span className="agg-text">{aggregationValues.min}</span></Box>
                                                <Box><span className="agg-text bold-text">Max:</span> <span className="agg-text">{aggregationValues.max}</span></Box>
                                                <Box><span className="agg-text bold-text">Sum:</span> <span className="agg-text">{aggregationValues.sum}</span></Box>
                                            </SpaceBetween>
                                        </Box>
                                        : ''
                                }
                                {
                                    gridInfo ? <Box float="left">
                                        <SpaceBetween direction="horizontal" size="s">
                                            <Box><span className="agg-text bold-text">Filtered rows:</span> <span className="agg-text">{gridInfo.rowCount}</span></Box>
                                            <Box><span className="agg-text bold-text">Total rows:</span> <span className="agg-text">{gridInfo.totalRows}</span></Box>
                                        </SpaceBetween>
                                    </Box> : ''
                                }
                            </div>
                        </div> : <></>
                }
            </SpaceBetween>
            <Modal
                onDismiss={() => resetSyncRequestModal()}
                visible={showSyncRequestCommentModal}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => resetSyncRequestModal()}> Cancel</Button>
                            <Button variant="primary" onClick={() => requestSync()}>Ok</Button>
                        </SpaceBetween>
                    </Box>
                }
                header={<SpaceBetween direction='horizontal' size='s'>
                    <Box variant='h3'>Comments</Box>
                    {
                        syncLoaderMessage ? <SpaceBetween direction='horizontal' size='s'>
                            <Box color="text-status-inactive"> <Spinner></Spinner> {syncLoaderMessage}</Box>
                        </SpaceBetween> : <></>
                    }

                </SpaceBetween>}
            >
                <Textarea
                    onChange={({ detail }) => setSyncRequestComment(detail.value)}
                    value={syncRequestComment}
                    placeholder={`Please provide a reason to sync from ${props?.tancDetails?.upstream_type}`}
                />

                {requestSyncCommentErrorMessage && (
                    <Box variant="p" color="text-status-error">
                        {requestSyncCommentErrorMessage}
                    </Box>
                )}

            </Modal>
            <Modal
                onDismiss={() => resetSaveRequestModal()}
                visible={showSaveModal}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => resetSaveRequestModal()}> Cancel</Button>
                            <Button variant="primary" onClick={() => saveAnnotated()}>Ok</Button>
                        </SpaceBetween>
                    </Box>
                }
                header={<SpaceBetween direction='horizontal' size='s'>
                    <Box variant='h3'>Comments</Box>
                </SpaceBetween>}
            >
                <Textarea
                    onChange={({ detail }) => setSaveRequestComment(detail.value)}
                    value={saveRequestComment}
                    placeholder={`Please provide a reason to sync from ${props?.tancDetails?.upstream_type}`}
                />

                {requestSaveCommentErrorMessage && (
                    <Box variant="p" color="text-status-error">
                        {requestSaveCommentErrorMessage}
                    </Box>
                )}

            </Modal>

            <Modal
                size="max"
                onDismiss={() => resetHistoryModal()}
                visible={showHistoryModal}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            {
                                historyErrorMessage ? <Box variant="p" color="text-status-error">{historyErrorMessage}</Box> : <></>
                            }
                            <Button variant="link" onClick={() => resetHistoryModal()}> Cancel</Button>
                        </SpaceBetween>
                    </Box>
                }
                header={
                    <Box variant='h3'>History</Box>}>
                {
                    historyLoaderMessage ? <SpaceBetween direction='horizontal' size='s'>
                        <Box color="text-status-inactive"> <Spinner></Spinner> {historyLoaderMessage}</Box>
                    </SpaceBetween> : <Table
                        columnDefinitions={[
                            {
                                id: "triggerType",
                                header: "Type",
                                cell: item => item.triggerType,
                                sortingField: "name",
                                isRowHeader: true
                            },
                            {
                                id: "triggerComments",
                                header: "Comment",
                                cell: item => item.triggerComments
                            },
                            {
                                id: "triggeredBy",
                                header: "Triggered by",
                                cell: item => item.triggeredBy,
                                sortingField: "alt"
                            },
                            {
                                id: "lastUpdatedTimestamp",
                                header: "Last updated",
                                cell: item => <SpaceBetween direction='horizontal' size='m'>
                                    <span className="bold">{getDate(item?.lastUpdatedTimestamp)}</span>
                                    {
                                        item?.delta?.fileUris?.find(uri => uri?.includes('resubmitted_')) ?

                                            <Popover
                                                dismissButton={false}
                                                position="top"
                                                size="small"
                                                triggerType="custom"
                                                content={
                                                    <Box>
                                                        {getDate(historyData?.find(versionItem => item?.delta?.fileUris?.find(uri => uri?.includes('resubmitted_'))?.includes(`_${versionItem?.['version']}.csv`))?.lastUpdatedTimestamp) || ''}
                                                    </Box>
                                                }
                                            >
                                                <span className="blue_badge">Resubmitted</span>
                                            </Popover>

                                            : <></>
                                    }
                                </SpaceBetween>,
                                minWidth: 170
                            },
                            {
                                id: "files",
                                header: "",
                                cell: item => <SpaceBetween direction='horizontal' size='m'>
                                    <Button onClick={() => { downloadVersionFile(item?.s3Path, item.version) }} disabled={!(isPortalAdmin || isSupportAdmin || tancUserRoleMap?.isAllowedUser)}>
                                        File
                                    </Button>
                                    <Button onClick={() => { resubmitVersionPrompt(item) }} variant="primary" disabled={!(isPortalAdmin || isSupportAdmin || tancUserRoleMap?.isAdmin || tancUserRoleMap?.isReviewer || tancUserRoleMap?.isEditor)}>
                                        Load 
                                    </Button>
                                    {
                                        item?.delta?.bucket && item?.delta?.fileUris ? <Button onClick={() => { downloadFile(item?.delta?.fileUris?.[0], item?.delta.bucket) }} variant="inline-link" disabled={!(isPortalAdmin || isSupportAdmin || tancUserRoleMap?.isAllowedUser)}>
                                            Change file
                                        </Button> : <></>
                                    }

                                </SpaceBetween>,
                                minWidth: 170
                            }
                        ]}
                        columnDisplay={[
                            // { id: "triggerType", visible: true },
                            { id: "triggeredBy", visible: true },
                            { id: "triggerComments", visible: true },
                            { id: "lastUpdatedTimestamp", visible: true },
                            { id: "files", visible: true },
                            { id: "transactionDetails", visible: true }
                        ]}
                        enableKeyboardNavigation
                        items={historyData}
                    />
                }
            </Modal>

            <Modal
                onDismiss={() => resetResubmitModal()}
                visible={resubmitVersionData}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => resetResubmitModal()}> Cancel</Button>
                            <Button variant="primary" onClick={() => resubmitVersion(resubmitVersionData)}>Proceed</Button>
                        </SpaceBetween>
                    </Box>
                }
                header={<SpaceBetween direction='horizontal' size='s'>
                    <Box variant='h3'>Important: Permanent Change Warning</Box>
                </SpaceBetween>}
            >
                <SpaceBetween size='m'>
                    {
                        (!(undoEditCount <= 0 || rowGroupColumns?.length > 0)) ? <Alert type="warning">
                            You have some unsaved work!
                        </Alert> : <></>
                    }
                    <Alert type="info">
                        This will overwrite your current unsaved work with data from <span className='boldText'>{getDate(resubmitVersionData?.lastUpdatedTimestamp)}</span>.
                    </Alert>
                </SpaceBetween>

            </Modal>

            <Modal
                onDismiss={() => setShowSaveCurrentFiltersModal(false)}
                visible={showSaveCurrentFiltersModal}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setShowSaveCurrentFiltersModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={() => {
                                saveCurrentFilters(currentFilterName)
                            }}>Save</Button>
                        </SpaceBetween>
                    </Box>
                }
                header="Save Current Filters"
            >
                <FormField label="Filter Name">
                    <Input                 
                        value={currentFilterName}
                        onChange={({ detail }) => {
                            setCurrentFilterName(detail.value);
                        }}
                    ></Input>
                </FormField>
            </Modal>
        </SpaceBetween>

    )
}

export default TANCSession;